import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useCanvasContext } from '../../contexts/CanvasContext';
import { useMicrophone } from '../../contexts/MicrophoneContext';
import SequenceDebug from './SequenceDebug';
import Subtitles from './Subtitles';
import useCSRF from '../../hooks/useCSRF';
import useHangup from '../../hooks/useHangup';
import { useSound, PHONE_AUDIO_VOLUME } from '../../contexts/SiteContext';
import { logger } from '../../utils/logger';
import { trackEvent } from '../../utils/analytics';
import { SOUND_OFF_PLAYBACK_TIMEOUT_MS, shouldApplySoundOffLimit } from '../../constants/audio';
import { HANGUP_CONFIG, QUESTIONS } from '../../config/questionConfig';

const clearTimeoutRef = (ref) => {
  if (ref.current) {
    clearTimeout(ref.current);
    ref.current = null;
  }
};

// Track when audio starts playing and START 12 second timeout
const HANGUP_TIMEOUT_MS = 12000; // Hardcoded 12 second timeout

const QuestionSequence = ({ onComplete, onHangup, showUI = true }) => {
  const { setQuestionState, setPhonePlaying, setAudioData, phoneboxState, releaseQuestionSequenceLock, getPhoneboxAudio } = useCanvasContext();
  const hangup = useHangup({ source: 'question_sequence' });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [questionState, setLocalQuestionState] = useState('idle'); // 'idle', 'playing', 'recording', 'processing'
  const [responses, setResponses] = useState([]);
  const [error, setError] = useState(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadIndicator, setShowUploadIndicator] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const isTranscribingRef = useRef(false); // Track isTranscribing to avoid unnecessary rerenders
  const [submissionId, setSubmissionId] = useState(null);
  const submissionIdRef = useRef(null);
  const [currentSubtitleText, setCurrentSubtitleText] = useState(null);
  const [currentSubtitleTimeouts, setCurrentSubtitleTimeouts] = useState(null);

  // Use the shared microphone context
  const {
    stream,
    permissionState,
    requestPermission: requestMicrophonePermission,
    hasLiveStream
  } = useMicrophone();
  const { isSoundOn } = useSound();

  // CSRF token management
  const { makeAuthenticatedRequest } = useCSRF();

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const questionAudioRef = useRef(null);
  const questionGainNodeRef = useRef(null);
  const mediaRecorderInitializedRef = useRef(false);
  const firstQuestionStartedRef = useRef(false);
  const playQuestionRef = useRef(null);
  const isAudioPlayingRef = useRef(false);
  const isQuestionAudioActiveRef = useRef(false);
  const questionAudioCooldownTimeoutRef = useRef(null);
  const questionReplayTimeoutRef = useRef(null);
  const startRecordingTimeoutRef = useRef(null);
  const pendingErrorTimeoutRef = useRef(null);
  const autoStopRecordingTimeoutRef = useRef(null);
  const nextQuestionTimeoutRef = useRef(null);
  const autoPlayNextQuestionTimeoutRef = useRef(null);
  const completionTimeoutRef = useRef(null);
  const phoneAudioStartTimeoutRef = useRef(null);
  const questionModeStartTimeoutRef = useRef(null);
  const currentQuestionTextRef = useRef(null); // Store current question text for subtitle
  const currentQuestionTimeoutsRef = useRef(null); // Store current question timeouts for subtitle
  const isLoadingAudioRef = useRef(false);
  const pendingErrorRef = useRef(null);
  const startRecordingRef = useRef(null);

  const recordingStartTimeRef = useRef(null);
  const maxDurationTimeoutRef = useRef(null);

  // Speech recognition refs
  const speechRecognitionRef = useRef(null);
  const speechRecognitionInitializedRef = useRef(false); // Track if speech recognition has been initialized
  const speechEndTimeoutRef = useRef(null);
  const isSpeechRecognitionActiveRef = useRef(false);
  const speechRecognitionRestartCountRef = useRef(0); // Track restart attempts to prevent infinite loops
  const speechRecognitionLastRestartTimeRef = useRef(0); // Track when we last restarted
  const lastSpeechTimeRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const hasValidSpeechRef = useRef(false);
  const hasConfirmedAnswerRef = useRef(false); // Tracks if we have a final result with >= 70% confidence
  const speechRecognitionWasActiveRef = useRef(false); // Track if speech recognition was ever active during current recording
  const speechFallbackTimeoutRef = useRef(null);
  const noSpeechTimeoutRef = useRef(null); // Timeout to stop recording if no speech detected
  const speechRecognitionStartedEarlyRef = useRef(false); // Track if we started speech recognition early (for user gesture preservation)
  const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(false);
  const [currentSpeechConfidence, setCurrentSpeechConfidence] = useState(0);
  const [detectedSpeech, setDetectedSpeech] = useState('');
  const [speechDebugInfo, setSpeechDebugInfo] = useState('');
  const [speechEndTimeoutActive, setSpeechEndTimeoutActive] = useState(false);
  const [confirmedTranscriptions, setConfirmedTranscriptions] = useState({});
  const [isCompleting, setIsCompleting] = useState(false);
  const isCompletingRef = useRef(false);
  const [submittedQuestions, setSubmittedQuestions] = useState(new Set());
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const questionSequenceLockRef = useRef(null);
  const phoneboxStateRef = useRef(phoneboxState);
  const shouldReplayQuestionRef = useRef(false);
  const hasValidAnswerRef = useRef(false); // Track if current recording has a valid answer (includes confirmed answer or valid speech fallback)
  // Note: manualReplayRequestedRef removed - we use hasValidAnswerRef.current = false when repeat is requested instead
  const uploadIndicatorTimeoutRef = useRef(null);
  const uploadIndicatorStartRef = useRef(null);
  const soundOffTimeoutRef = useRef(null); // Timeout for sound-off playback limit
  const isSoundOnRef = useRef(isSoundOn); // Track current sound state
  const isHungUpRef = useRef(false); // Track if phone has been hung up to prevent race conditions
  const audioEndedTimeRef = useRef(null); // Track when audio ended to ignore speech recognition results during cooldown
  const handleHangupResetRef = useRef(null); // Ref to hold hangup reset function for use in speech recognition handlers
  const hangupAudioRef = useRef(null); // Ref to store hangup audio to prevent cleanup from stopping it
  const hangupTimeoutRef = useRef(null); // Ref to store hangup timeout to allow clearing it
  const pendingUploadsRef = useRef(new Map()); // Track pending upload promises by questionId

  // Ensure upload notice displays for at least 2 seconds
  useEffect(() => {
    if (isUploading) {
      if (uploadIndicatorTimeoutRef.current) {
        clearTimeout(uploadIndicatorTimeoutRef.current);
        uploadIndicatorTimeoutRef.current = null;
      }
      uploadIndicatorStartRef.current = Date.now();
      setShowUploadIndicator(true);
    } else if (uploadIndicatorStartRef.current) {
      const elapsed = Date.now() - uploadIndicatorStartRef.current;
      const remaining = Math.max(0, 2000 - elapsed);
      if (remaining === 0) {
        setShowUploadIndicator(false);
        uploadIndicatorStartRef.current = null;
      } else {
        if (uploadIndicatorTimeoutRef.current) {
          clearTimeout(uploadIndicatorTimeoutRef.current);
        }
        uploadIndicatorTimeoutRef.current = setTimeout(() => {
          setShowUploadIndicator(false);
          uploadIndicatorTimeoutRef.current = null;
          uploadIndicatorStartRef.current = null;
        }, remaining);
      }
    } else {
      setShowUploadIndicator(false);
    }

    return () => {
      if (uploadIndicatorTimeoutRef.current) {
        clearTimeout(uploadIndicatorTimeoutRef.current);
        uploadIndicatorTimeoutRef.current = null;
      }
    };
  }, [isUploading]);

  // Ensure microphone stream is live when permission is granted (mobile recovery)
  // Only request microphone when phone is off hook
  useEffect(() => {
    if (permissionState === 'granted' && !hasLiveStream && requestMicrophonePermission && phoneboxState.isOffHook) {
      logger.debug('Microphone permission granted but stream is inactive - attempting to reinitialize');
      requestMicrophonePermission();
    }
  }, [permissionState, hasLiveStream, requestMicrophonePermission, phoneboxState.isOffHook]);
  const QUESTION_AUDIO_COOLDOWN_MS = 150; // Delay before speech recognition resumes after prompt
  const SPEECH_RECOGNITION_COOLDOWN_MS = 1000; // Delay after audio ends before accepting speech recognition results (prevents detecting question audio)
  const MAX_REPEAT_PREFIX_WORDS = 3;

  // Memoize REPEAT_REQUEST_PATTERNS to prevent it from changing on every render
  const REPEAT_REQUEST_PATTERNS = useMemo(() => [
    /^repeat(?:\s+(?:that|it|the question))?(?:\s+please)?$/i,
    /^(?:can|could|would)\s+(?:you|ya|u)\s+repeat(?:\s+(?:that|it|the question))?(?:\s+please)?$/i,
    /^(?:say|tell)\s+(?:that|it)\s+again(?:\s+please)?$/i,
    /^come\s+again(?:\s+please)?$/i,
    /^what\s+did\s+you\s+say(?:\s+please)?$/i,
    /^what\s+(?:was|were)\s+(?:that|the\s+question|you\s+saying)(?:\s+please)?$/i
  ], []);

  // Speech recognition configuration
  const SPEECH_END_TIMEOUT = 800; // 0.8 seconds after last speech to stop recording (faster progression)
  const MIN_SPEECH_DURATION = 0; // No minimum speech duration - allow very short answers
  const SPEECH_CONFIDENCE_THRESHOLD = 0.3; // Minimum confidence for speech detection (lowered for better detection)
  const SPEECH_CONFIRMATION_THRESHOLD = 0.7; // 70% confidence required to confirm a valid answer

  // Use questions from configuration file
  const questions = useMemo(() => QUESTIONS, []);

  // Detect Android Chrome - defined early as it's used in multiple callbacks
  const isAndroidChrome = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android/i.test(userAgent) && /chrome/i.test(userAgent) && !/firefox/i.test(userAgent);
  }, []);

  // Stop recording function - defined early to avoid hoisting issues
  const stopRecording = useCallback(() => {
    logger.debug('stopRecording called', {
      hasMediaRecorder: !!mediaRecorderRef.current,
      recorderState: mediaRecorderRef.current?.state,
      isRecording,
      currentQuestionIndex: currentQuestionIndexRef.current,
      totalQuestions: questionsRef.current.length,
      isFinalQuestion: currentQuestionIndexRef.current === questionsRef.current.length - 1
    });

    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
      logger.debug('Not recording or no media recorder, skipping stop', {
        recorderState: mediaRecorderRef.current?.state,
        isRecording
      });

      // IMPORTANT: If recording already stopped but we have a valid answer and it's the final question,
      // we need to ensure processResponse is called. Check if onstop handler already fired by checking
      // if we have audio chunks (onstop clears them)
      const hasAudioChunks = audioChunksRef.current && audioChunksRef.current.length > 0;
      const isFinalQuestion = currentQuestionIndexRef.current === questionsRef.current.length - 1;
      const hasValidAnswer = hasValidAnswerRef.current || hasConfirmedAnswerRef.current;
      const currentQuestionId = questionsRef.current[currentQuestionIndexRef.current]?.id;

      // Only manually trigger if:
      // 1. It's the final question
      // 2. We have a valid answer
      // 3. We have audio chunks (onstop hasn't fired yet - if it fired, chunks would be cleared)
      // 4. Recording is not active
      // 5. processResponseRef is available
      if (isFinalQuestion && hasValidAnswer && hasAudioChunks && !isRecording && processResponseRef.current) {
        logger.debug('Recording already stopped but final question with valid answer - manually triggering onstop logic', {
          hasAudioChunks,
          isFinalQuestion,
          hasValidAnswer,
          currentQuestionId,
          audioChunksCount: audioChunksRef.current.length
        });
        // Manually trigger the onstop handler logic
        // Create response from existing chunks
        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm;codecs=opus';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);

        const response = {
          questionId: currentQuestionId,
          questionText: questionsRef.current[currentQuestionIndexRef.current].text,
          questionAudioFile: questionsRef.current[currentQuestionIndexRef.current].audioFile,
          audioBlob,
          audioUrl,
          duration: recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0,
          timestamp: new Date().toISOString()
        };

        // Process the response - this will call completeQuestionSequence for final question
        processResponseRef.current(response);
        return;
      } else if (isFinalQuestion && hasValidAnswer && !hasAudioChunks) {
        logger.debug('Recording stopped, final question with valid answer but no audio chunks - onstop handler likely already fired');
      }

      return;
    }

    // Clear any existing timeouts
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }

    if (speechEndTimeoutRef.current) {
      clearTimeout(speechEndTimeoutRef.current);
      speechEndTimeoutRef.current = null;
    }

    // Stop speech recognition if available
    // ANDROID: Don't stop speech recognition here - it runs continuously until hangup
    if (isAndroidChrome) {
      logger.debug('Android: Keeping speech recognition running continuously (will stop on hangup)');
      // Don't stop speech recognition on Android - it runs continuously
    } else if (speechRecognitionRef.current && isSpeechRecognitionActiveRef.current) {
      // Non-Android: Stop speech recognition when recording stops
      try {
        speechRecognitionRef.current.stop();
        isSpeechRecognitionActiveRef.current = false;
        setIsTranscribing(false);
        isTranscribingRef.current = false;
        speechRecognitionStartedEarlyRef.current = false; // Reset early start flag
        logger.debug('Stopped speech recognition');
      } catch (error) {
        console.error('Error stopping speech recognition:', error);
        speechRecognitionStartedEarlyRef.current = false; // Reset even on error
      }
    } else {
      // Reset early start flag even if speech recognition wasn't active (non-Android only)
      speechRecognitionStartedEarlyRef.current = false;
    }

    if (speechFallbackTimeoutRef.current) {
      clearTimeout(speechFallbackTimeoutRef.current);
      speechFallbackTimeoutRef.current = null;
    }

    if (noSpeechTimeoutRef.current) {
      clearTimeout(noSpeechTimeoutRef.current);
      noSpeechTimeoutRef.current = null;
    }

    // Check if there was a valid answer before stopping
    // For speech recognition: check if we have a confirmed answer (>= 70% confidence final result)
    let hasValidAnswer = hasConfirmedAnswerRef.current;

    // Calculate recording duration
    const recordingDuration = recordingStartTimeRef.current ? Date.now() - recordingStartTimeRef.current : 0;
    const MIN_RECORDING_DURATION_FOR_ACCEPTANCE = 2000; // 2 seconds minimum

    logger.debug('Checking for valid answer (speech recognition):', {
      hasValidAnswer,
      hasConfirmedAnswer: hasConfirmedAnswerRef.current,
      hasValidSpeech: hasValidSpeechRef.current,
      speechRecognitionWasActive: speechRecognitionWasActiveRef.current,
      recordingDuration
    });

    console.log('[DEBUG] stopRecording - Before processing:', {
      hasValidAnswer,
      hasConfirmedAnswer: hasConfirmedAnswerRef.current,
      hasValidSpeech: hasValidSpeechRef.current,
      speechRecognitionWasActive: speechRecognitionWasActiveRef.current,
      recordingDuration,
      shouldReplay: shouldReplayQuestionRef.current
    });

    // If hasValidAnswer is already false (e.g., from handleRepeatRequest), keep it false
    // Otherwise, check if we should use valid speech as fallback
    if (!hasValidAnswer) {
      // Check if we should use valid speech as fallback
      console.log('[DEBUG] stopRecording - Checking fallback condition:', {
        hasValidAnswer,
        hasValidSpeech: hasValidSpeechRef.current,
        conditionMet: !hasValidAnswer && hasValidSpeechRef.current
      });

      if (!hasValidAnswer && hasValidSpeechRef.current) {
        // If we have valid speech but no confirmed answer, and manual replay was NOT requested,
        // use valid speech as a fallback to set hasValidAnswer = true
        // This handles cases where speech recognition had issues (like "aborted" error) but user gave valid answer
        console.log('[DEBUG] stopRecording - No confirmed answer but has valid speech - using as fallback for hasValidAnswer');
        hasValidAnswer = true;
      } else if (!hasValidAnswer &&
        speechRecognitionWasActiveRef.current &&
        recordingDuration >= MIN_RECORDING_DURATION_FOR_ACCEPTANCE) {
        // If speech recognition was active but aborted/failed, and we've been recording for a reasonable duration,
        // accept the recording anyway (audio is still being captured even if speech recognition failed)
        // This handles cases where speech recognition aborted but user was speaking
        console.log('[DEBUG] stopRecording - Speech recognition was active but no speech detected - accepting recording due to duration:', {
          recordingDuration,
          minDuration: MIN_RECORDING_DURATION_FOR_ACCEPTANCE
        });
        logger.debug('Accepting recording despite no detected speech - speech recognition was active and recording duration is sufficient');
        hasValidAnswer = true;
      } else {
        console.log('[DEBUG] stopRecording - Fallback condition NOT met:', {
          reason: hasValidAnswer ? 'hasValidAnswer is true (already set)' :
            !speechRecognitionWasActiveRef.current ? 'speech recognition was never active' :
              recordingDuration < MIN_RECORDING_DURATION_FOR_ACCEPTANCE ? 'recording too short' :
                'hasValidSpeech is false',
          hasValidAnswer,
          hasValidSpeech: hasValidSpeechRef.current,
          speechRecognitionWasActive: speechRecognitionWasActiveRef.current,
          recordingDuration
        });
      }
    }

    // Store valid answer status for use in onstop handler
    hasValidAnswerRef.current = hasValidAnswer;

    // Set replay flag: replay if we have no valid answer and no valid speech
    // Don't replay if speech recognition was active and recording was long enough (handled above)
    const shouldForceReplay = !hasValidAnswer && !hasValidSpeechRef.current;
    shouldReplayQuestionRef.current = shouldForceReplay;

    console.log('[DEBUG] stopRecording - After processing:', {
      hasValidAnswer,
      hasValidAnswerRef: hasValidAnswerRef.current,
      shouldReplay: shouldReplayQuestionRef.current,
      hasValidSpeech: hasValidSpeechRef.current,
      shouldForceReplay
    });

    if (!hasValidAnswer) {
      logger.debug('No valid answer detected - will replay question');
    }

    // Reset speech tracking variables
    lastSpeechTimeRef.current = null;
    speechStartTimeRef.current = null;
    hasValidSpeechRef.current = false;
    hasConfirmedAnswerRef.current = false; // Reset confirmed answer flag
    setCurrentSpeechConfidence(0);
    setSpeechEndTimeoutActive(false);
    setDetectedSpeech('');
    setSpeechDebugInfo('');

    mediaRecorderRef.current.stop();
    setIsRecording(false);
    setLocalQuestionState('processing');

    logger.debug('Stopped recording');
  }, [isAndroidChrome, isRecording]);

  // Create refs after questions are defined
  const questionsRef = useRef(questions);
  const currentQuestionIndexRef = useRef(currentQuestionIndex);
  const trackQuestionEvent = useCallback((eventName, params = {}) => {
    trackEvent(eventName, {
      event_category: 'question_sequence',
      submission_id: submissionIdRef.current,
      question_index: currentQuestionIndexRef.current + 1,
      ...params
    });
  }, []);

  const scheduleQuestionReplay = useCallback((manual = false) => {
    const delay = manual ? 200 : 1000;
    logger.debug('Scheduling question replay', { manual, delay });

    clearTimeoutRef(questionReplayTimeoutRef);

    questionReplayTimeoutRef.current = setTimeout(() => {
      questionReplayTimeoutRef.current = null;
      if (!phoneboxStateRef.current.isOffHook) {
        logger.debug('Replay cancelled - phone is on hook');
        return;
      }

      if (questionAudioRef.current) {
        try {
          questionAudioRef.current.pause();
          questionAudioRef.current.currentTime = 0;
        } catch (error) {
          logger.warn('Error resetting question audio before replay:', error);
        }
      }

      if (playQuestionRef.current) {
        playQuestionRef.current();
      } else {
        logger.warn('playQuestionRef unavailable during replay');
      }
    }, delay);
  }, []);

  const markQuestionAudioInactive = useCallback((immediate = false) => {
    clearTimeoutRef(questionAudioCooldownTimeoutRef);

    if (immediate || QUESTION_AUDIO_COOLDOWN_MS <= 0) {
      isQuestionAudioActiveRef.current = false;
      return;
    }

    questionAudioCooldownTimeoutRef.current = setTimeout(() => {
      isQuestionAudioActiveRef.current = false;
      questionAudioCooldownTimeoutRef.current = null;
    }, QUESTION_AUDIO_COOLDOWN_MS);
  }, []);

  const scheduleStartRecording = useCallback((delay = 500) => {
    clearTimeoutRef(startRecordingTimeoutRef);
    startRecordingTimeoutRef.current = setTimeout(() => {
      startRecordingTimeoutRef.current = null;
      // Clear audio ended time when starting recording (cooldown period has passed)
      audioEndedTimeRef.current = null;
      if (startRecordingRef.current) {
        startRecordingRef.current();
      }
    }, delay);
  }, []);


  const clearQuestionAdvanceTimers = useCallback(() => {
    clearTimeoutRef(nextQuestionTimeoutRef);
    clearTimeoutRef(autoPlayNextQuestionTimeoutRef);
    clearTimeoutRef(completionTimeoutRef);
  }, []);

  // Helper function to clear all timeouts
  const clearAllTimeouts = useCallback(() => {
    clearTimeoutRef(maxDurationTimeoutRef);
    clearTimeoutRef(speechEndTimeoutRef);
    clearTimeoutRef(speechFallbackTimeoutRef);
    clearTimeoutRef(noSpeechTimeoutRef);
    clearTimeoutRef(autoStopRecordingTimeoutRef);
    clearTimeoutRef(startRecordingTimeoutRef);
    clearTimeoutRef(questionAudioCooldownTimeoutRef);
    clearTimeoutRef(questionReplayTimeoutRef);
    clearTimeoutRef(soundOffTimeoutRef);
    clearTimeoutRef(pendingErrorTimeoutRef);
    clearTimeoutRef(uploadIndicatorTimeoutRef);
    clearTimeoutRef(nextQuestionTimeoutRef);
    clearTimeoutRef(autoPlayNextQuestionTimeoutRef);
    clearTimeoutRef(completionTimeoutRef);
    clearTimeoutRef(phoneAudioStartTimeoutRef);
    clearTimeoutRef(questionModeStartTimeoutRef);
    // Note: hangupTimeoutRef is NOT cleared here - it's managed separately in completeQuestionSequence
  }, []);

  const replayQuestion = useCallback(({ manual = false, source = 'manual_request' } = {}) => {
    logger.debug('replayQuestion invoked', {
      manual,
      source,
      isRecording,
      recorderState: mediaRecorderRef.current?.state
    });

    console.log('[DEBUG] replayQuestion - Setting replay flags and clearing all timeouts:', {
      manual,
      source,
      previousShouldReplay: shouldReplayQuestionRef.current,
      previousHasValidAnswer: hasValidAnswerRef.current,
      previousHasConfirmedAnswer: hasConfirmedAnswerRef.current
    });

    // Set replay flags - invalidate any answer and mark for replay
    shouldReplayQuestionRef.current = true;
    hasValidAnswerRef.current = false;
    hasConfirmedAnswerRef.current = false;
    hasValidSpeechRef.current = false;
    speechRecognitionStartedEarlyRef.current = false; // Reset early start flag on replay

    // Reset speech tracking refs
    lastSpeechTimeRef.current = null;
    speechStartTimeRef.current = null;
    speechRecognitionWasActiveRef.current = false; // Reset tracking for replay

    // Clear all question advance timers
    clearQuestionAdvanceTimers();

    // Clear all timeouts
    clearAllTimeouts();

    // Reset speech-related state
    setSpeechEndTimeoutActive(false);
    setSpeechDebugInfo(manual ? 'Repeat requested - replaying question' : 'Replaying question');
    setDetectedSpeech('');
    setCurrentSpeechConfidence(0);

    // Stop and reset question audio
    if (questionAudioRef.current) {
      try {
        questionAudioRef.current.pause();
        questionAudioRef.current.currentTime = 0;
        isQuestionAudioActiveRef.current = false;
        isAudioPlayingRef.current = false;
        setIsPlayingAudio(false);
      } catch (error) {
        logger.warn('Error stopping question audio during replayQuestion:', error);
      }
    }

    // Stop speech recognition if active
    // ANDROID: Don't stop speech recognition - it runs continuously
    if (isAndroidChrome) {
      logger.debug('Android: Keeping speech recognition running during replay');
      speechRecognitionStartedEarlyRef.current = false; // Reset flag
    } else if (speechRecognitionRef.current && isSpeechRecognitionActiveRef.current) {
      try {
        speechRecognitionRef.current.stop();
        isSpeechRecognitionActiveRef.current = false;
        setIsTranscribing(false);
        isTranscribingRef.current = false;
        speechRecognitionStartedEarlyRef.current = false; // Reset early start flag
        logger.debug('Stopped speech recognition during replayQuestion');
      } catch (error) {
        console.error('Error stopping speech recognition during replayQuestion:', error);
        speechRecognitionStartedEarlyRef.current = false; // Reset even on error
      }
    } else {
      speechRecognitionStartedEarlyRef.current = false; // Reset even if not active
    }

    if (isRecording && mediaRecorderRef.current?.state === 'recording') {
      console.log('[DEBUG] replayQuestion - Recording is active, calling stopRecording()');
      stopRecording();
    } else {
      console.log('[DEBUG] replayQuestion - Recording not active, scheduling replay');
      scheduleQuestionReplay(manual);
    }
  }, [clearQuestionAdvanceTimers, isRecording, scheduleQuestionReplay, stopRecording, clearAllTimeouts, isAndroidChrome]);

  const isRepeatRequest = useCallback((rawTranscript) => {
    if (!rawTranscript) return false;

    const cleaned = rawTranscript
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) {
      return false;
    }

    const words = cleaned.split(' ');
    const maxOffset = Math.min(MAX_REPEAT_PREFIX_WORDS, words.length - 1);

    for (let offset = 0; offset <= maxOffset; offset += 1) {
      const candidate = words.slice(offset).join(' ');
      if (candidate.length === 0) continue;
      if (REPEAT_REQUEST_PATTERNS.some(pattern => pattern.test(candidate))) {
        logger.debug('Repeat pattern matched', { candidate, offset });
        return true;
      }
    }

    return false;
  }, [REPEAT_REQUEST_PATTERNS]);

  const handleRepeatRequest = useCallback((transcript) => {
    logger.debug('Repeat request detected, replaying question:', transcript);
    // Immediately invalidate any answer that was detected - repeat request means user wants to start over
    hasValidAnswerRef.current = false;
    hasConfirmedAnswerRef.current = false;
    hasValidSpeechRef.current = false;
    trackQuestionEvent('question_repeat_requested', {
      transcript: transcript?.trim() || ''
    });
    replayQuestion({ manual: true, source: 'speech_repeat_request' });
  }, [replayQuestion, trackQuestionEvent]);
  const processResponseRef = useRef(null);

  // Force stop speech recognition
  const forceStopSpeechRecognition = useCallback(() => {
    if (speechRecognitionRef.current) {
      try {
        // Use abort() instead of stop() to immediately stop recognition and discard any pending results
        // stop() may still process and return results for audio already captured
        // abort() immediately stops and discards everything, which is what we want
        speechRecognitionRef.current.abort();
        isSpeechRecognitionActiveRef.current = false;
        setIsTranscribing(false);
        isTranscribingRef.current = false;
        speechRecognitionStartedEarlyRef.current = false; // Reset early start flag
        logger.debug('Force stopped speech recognition (aborted)');
      } catch (error) {
        console.error('Error force stopping speech recognition:', error);
        // Fallback to stop() if abort() fails
        try {
          speechRecognitionRef.current.stop();
          isSpeechRecognitionActiveRef.current = false;
          setIsTranscribing(false);
          isTranscribingRef.current = false;
          speechRecognitionStartedEarlyRef.current = false; // Reset early start flag
          logger.debug('Force stopped speech recognition (fallback to stop)');
        } catch (stopError) {
          console.error('Error stopping speech recognition (fallback):', stopError);
          speechRecognitionStartedEarlyRef.current = false; // Reset even on error
        }
      }
    } else {
      speechRecognitionStartedEarlyRef.current = false; // Reset even if no recognition ref
    }
  }, []);
  // Initialize single audio player
  const initializeAudioPlayer = useCallback(() => {
    if (questionAudioRef.current) {
      return; // Already initialized
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.muted = false; // Keep unmuted so analyser can analyze audio
    audio.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
    // Volume is controlled by gainNode, not audio element volume
    questionAudioRef.current = audio;

    // Set up event listeners once - use named functions to prevent duplicate listeners
    const handleLoadStart = () => {
      // CRITICAL: Ignore loadstart events during hangup sequence completion
      // When we clear question audio src during completion, it triggers loadstart with empty/invalid src
      if (isCompletingRef.current) {
        logger.debug('Ignoring question audio loadstart during hangup sequence');
        return;
      }

      logger.audio('Audio loading started:', audio.src);
      setIsLoadingAudio(true);
      isLoadingAudioRef.current = true;
      setLocalQuestionState('loading');
      // Clear any pending errors when starting to load
      pendingErrorRef.current = null;
      setError(null);
    };

    const handleCanPlay = () => {
      logger.audio('Audio can play:', audio.src);
      setIsLoadingAudio(false);
      isLoadingAudioRef.current = false;
      clearTimeoutRef(pendingErrorTimeoutRef);
      // Clear any pending errors when audio can play
      if (pendingErrorRef.current) {
        logger.debug('Clearing pending error - audio loaded successfully');
        pendingErrorRef.current = null;
        setError(null);
      }
    };

    const handlePlay = async () => {
      // Check if phone has been hung up - if so, stop audio immediately
      if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
        logger.debug('Audio play event fired but phone is hung up - stopping audio immediately');
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.src = '';
        } catch (error) {
          console.warn('Error stopping audio in handlePlay after hangup:', error);
        }
        isAudioPlayingRef.current = false;
        setIsPlayingAudio(false);
        markQuestionAudioInactive(true);
        return;
      }

      logger.audio('Audio started playing:', audio.src);
      setIsLoadingAudio(false);
      isLoadingAudioRef.current = false;
      clearTimeoutRef(pendingErrorTimeoutRef);
      setIsPlayingAudio(true);
      isAudioPlayingRef.current = true;
      if (questionAudioCooldownTimeoutRef.current) {
        clearTimeout(questionAudioCooldownTimeoutRef.current);
        questionAudioCooldownTimeoutRef.current = null;
      }
      isQuestionAudioActiveRef.current = true;
      setLocalQuestionState('playing');
      setPhonePlaying(true);

      // Set subtitle text and timeouts for current question (use ref to get latest value)
      if (currentQuestionTextRef.current) {
        setCurrentSubtitleText(currentQuestionTextRef.current);
        setCurrentSubtitleTimeouts(currentQuestionTimeoutsRef.current);
      }
      // Clear any pending errors when audio starts playing
      if (pendingErrorRef.current) {
        logger.debug('Clearing pending error - audio started playing');
        pendingErrorRef.current = null;
        setError(null);
      }

      // Connect audio to analyser for soundwaves visualization
      const connectAudioToAnalyser = async () => {
        // Don't connect if phone has been hung up
        if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
          logger.debug('Skipping audio analyser connection - phone is hung up');
          return;
        }

        if (phoneboxState.audioContext && phoneboxState.analyser && !audio.connectedToAnalyser) {
          try {
            // Double-check hangup state before resuming audio context
            if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
              logger.debug('Skipping audio context resume - phone is hung up');
              return;
            }

            // Ensure audio context is resumed before connecting
            if (phoneboxState.audioContext.state === 'suspended') {
              await phoneboxState.audioContext.resume();
              logger.audio('Audio context resumed before connecting question audio to analyser');
            }

            // Final check before connecting
            if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
              logger.debug('Skipping audio analyser connection - phone hung up during async operation');
              return;
            }

            // createMediaElementSource can only be called once per audio element
            // If it throws, it means it's already connected, which is fine
            try {
              const source = phoneboxState.audioContext.createMediaElementSource(audio);

              // Create gain node to control volume without affecting analyser
              const gainNode = phoneboxState.audioContext.createGain();
              questionGainNodeRef.current = gainNode;

              // Split the source: connect to analyser for analysis (always full signal)
              source.connect(phoneboxState.analyser);

              // Also connect source to gain node, then to destination (gain controls user volume)
              source.connect(gainNode);
              gainNode.connect(phoneboxState.audioContext.destination);

              // Set initial gain based on sound state
              gainNode.gain.value = isSoundOn ? 1 : 0;

              logger.audio('Question audio connected to analyser for soundwaves visualization');
            } catch (connectError) {
              // If already connected, this is expected and not an error
              if (connectError.name === 'InvalidStateError' || connectError.message?.includes('already been connected')) {
                logger.audio('Audio element already connected to analyser (this is expected)');
              } else {
                // Re-throw unexpected errors
                throw connectError;
              }
            }

            // Mark as connected to prevent multiple connection attempts
            audio.connectedToAnalyser = true;
          } catch (error) {
            // Log but don't treat connection errors as fatal - audio can still play without analyser
            console.warn('Could not connect question audio to analyser (audio will still play):', error);
            // Mark as connected anyway to prevent retry attempts
            audio.connectedToAnalyser = true;
          }
        }
      };

      // Connect to analyser (non-blocking - don't await to avoid delaying audio playback)
      connectAudioToAnalyser().catch(err => {
        // Silently handle any connection errors - audio playback should continue
        console.warn('Analyser connection error (non-fatal):', err);
      });

      // Clear any error when audio successfully starts playing
      setError(null);

      // Force stop speech recognition while playing audio
      // ANDROID: Don't stop speech recognition - it runs continuously, but results will be ignored during audio
      if (!isAndroidChrome) {
        forceStopSpeechRecognition();

        // Set up periodic check to ensure speech recognition stays stopped while audio is playing
        const speechCheckInterval = setInterval(() => {
          // Stop checking if phone has been hung up
          if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
            clearInterval(speechCheckInterval);
            audio.speechCheckInterval = null;
            return;
          }

          if ((isAudioPlayingRef.current || isQuestionAudioActiveRef.current) && isSpeechRecognitionActiveRef.current) {
            logger.debug('Speech recognition restarted during audio playback - force stopping again');
            forceStopSpeechRecognition();
          }
        }, 100); // Check every 100ms

        // Store interval ID for cleanup
        audio.speechCheckInterval = speechCheckInterval;
      } else {
        logger.debug('Android: Keeping speech recognition running during audio playback (results will be ignored)');
        // Android: Still need to store null to prevent errors
        audio.speechCheckInterval = null;
      }

      // If sound is off, limit playback to the configured timeout
      if (!isSoundOnRef.current && shouldApplySoundOffLimit(audio, SOUND_OFF_PLAYBACK_TIMEOUT_MS, logger.audio)) {
        logger.audio(`Sound is off - limiting audio playback to ${SOUND_OFF_PLAYBACK_TIMEOUT_MS / 1000} seconds`);
        // Clear any existing timeout
        if (soundOffTimeoutRef.current) {
          clearTimeout(soundOffTimeoutRef.current);
        }
        // Set timeout to manually end audio after the configured limit
        soundOffTimeoutRef.current = setTimeout(() => {
          // Check if phone has been hung up - if so, don't process timeout
          if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
            logger.debug('Sound-off timeout fired but phone is hung up - ignoring');
            soundOffTimeoutRef.current = null;
            return;
          }

          // Check if audio is still playing and sound is still off
          if (audio && !audio.paused && audio.readyState >= 2 && !isSoundOnRef.current && isAudioPlayingRef.current) {
            logger.audio(`${SOUND_OFF_PLAYBACK_TIMEOUT_MS / 1000}-second limit reached (sound off) - ending audio playback`);
            // Pause the audio
            audio.pause();
            // Manually trigger the ended handler
            handleEnded();
          }
          soundOffTimeoutRef.current = null;
        }, SOUND_OFF_PLAYBACK_TIMEOUT_MS);
      }
    };

    const handleEnded = () => {
      // Check if phone has been hung up - if so, don't start recording
      if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
        logger.debug('Audio ended but phone is hung up - not starting recording');
        setIsPlayingAudio(false);
        isAudioPlayingRef.current = false;
        markQuestionAudioInactive(true);
        setPhonePlaying(false);

        // Clear subtitle text and timeouts when question audio ends
        setCurrentSubtitleText(null);
        setCurrentSubtitleTimeouts(null);

        // Clear the speech check interval
        if (audio.speechCheckInterval) {
          clearInterval(audio.speechCheckInterval);
          audio.speechCheckInterval = null;
          logger.debug('Cleared speech check interval');
        }

        // Clear the sound-off timeout if it exists
        if (soundOffTimeoutRef.current) {
          clearTimeout(soundOffTimeoutRef.current);
          soundOffTimeoutRef.current = null;
        }
        return;
      }

      logger.audio('Audio ended:', audio.src);
      setIsPlayingAudio(false);
      isAudioPlayingRef.current = false;
      markQuestionAudioInactive();
      setLocalQuestionState('idle');
      setPhonePlaying(false);

      // Mark when audio ended to ignore speech recognition results during cooldown
      audioEndedTimeRef.current = Date.now();

      // Clear subtitle text and timeouts when question audio ends
      setCurrentSubtitleText(null);
      setCurrentSubtitleTimeouts(null);

      // Clear the speech check interval
      if (audio.speechCheckInterval) {
        clearInterval(audio.speechCheckInterval);
        audio.speechCheckInterval = null;
        logger.debug('Cleared speech check interval');
      }

      // Clear the sound-off timeout if it exists
      if (soundOffTimeoutRef.current) {
        clearTimeout(soundOffTimeoutRef.current);
        soundOffTimeoutRef.current = null;
      }

      // USER GESTURE PRESERVATION: Start speech recognition immediately when audio ends
      // This preserves the event handler context which is closer to user gesture
      // This is critical for Android Chrome which requires user gesture to start speech recognition
      // NOTE: On Android, speech recognition is already running continuously, so skip this
      if (!isAndroidChrome &&
        speechRecognitionSupported &&
        !isCompletingRef.current &&
        !isPlayingAudio &&
        !isAudioPlayingRef.current &&
        !isQuestionAudioActiveRef.current &&
        speechRecognitionRef.current &&
        !isSpeechRecognitionActiveRef.current &&
        phoneboxStateRef.current.isOffHook) {
        try {
          logger.debug('Starting speech recognition early (user gesture preservation) - audio ended');
          speechRecognitionWasActiveRef.current = true;
          speechRecognitionStartedEarlyRef.current = true;
          speechRecognitionRef.current.start();
          logger.debug('Speech recognition started early - will be active when recording starts');
        } catch (earlyStartError) {
          logger.warn('Failed to start speech recognition early (will retry when recording starts):', earlyStartError);
          speechRecognitionStartedEarlyRef.current = false;
          // Don't throw - we'll try again when recording actually starts
        }
      } else if (isAndroidChrome && isSpeechRecognitionActiveRef.current) {
        logger.debug('Android: Speech recognition already running continuously - skipping early start');
      }

      // Start recording after audio ends - use a ref to avoid dependency issues
      // Use longer delay to ensure speech recognition cooldown has passed
      scheduleStartRecording(Math.max(500, SPEECH_RECOGNITION_COOLDOWN_MS + 200));
    };

    const handleError = (error) => {
      const errorTarget = error?.target || error?.currentTarget;
      const errorSrc = errorTarget?.src || '';

      // CRITICAL: Check if we're completing the sequence FIRST - ignore ALL errors during hangup
      // This prevents hangup audio errors from triggering question audio error handling
      if (isCompletingRef.current) {
        logger.debug('Ignoring audio error during hangup sequence (completing)', {
          errorSrc,
          isCompleting: isCompletingRef.current,
          errorTarget: errorTarget === questionAudioRef.current ? 'questionAudio' :
            errorTarget === hangupAudioRef.current ? 'hangupAudio' : 'unknown'
        });
        return;
      }

      // Also ignore errors from empty/invalid src (happens when we clear question audio src during completion)
      // Empty src resolves to the current page URL (e.g., "http://localhost:3000/")
      const isInvalidSrc = errorSrc === window.location.href ||
        errorSrc === window.location.origin + '/' ||
        (errorSrc.endsWith('/') && !errorSrc.includes('/audio/'));
      if (isInvalidSrc && errorTarget === questionAudioRef.current) {
        logger.debug('Ignoring error from invalid/cleared question audio src', {
          errorSrc,
          currentUrl: window.location.href
        });
        return;
      }

      // Check if this error is from hangup audio (not question audio)
      // Priority: Check ref equality first (most reliable)
      const isHangupAudioRef = hangupAudioRef.current && errorTarget === hangupAudioRef.current;
      const isQuestionAudioRef = questionAudioRef.current && errorTarget === questionAudioRef.current;

      // Also check if the src indicates hangup audio (in case ref isn't set yet)
      const isHangupAudioSrc = errorSrc.includes('hangup.mp3');

      // If error is from hangup audio ref or src, ignore it
      if (isHangupAudioRef || isHangupAudioSrc) {
        logger.debug('Ignoring hangup audio error in question audio handler', {
          isHangupAudioRef,
          isHangupAudioSrc,
          errorSrc
        });
        return;
      }

      // If error is from question audio ref, process it
      if (isQuestionAudioRef) {
        // Continue to process question audio error
      } else {
        // Error target doesn't match either ref - if src suggests hangup audio, ignore it
        if (isHangupAudioSrc) {
          logger.debug('Ignoring error - src indicates hangup audio', {
            errorSrc,
            isCompleting: isCompletingRef.current
          });
          return;
        }
      }

      console.error('Audio error event triggered:', error);
      console.error('Error details:', {
        type: error?.type,
        message: error?.message,
        code: error?.code,
        target: error?.target,
        error: error?.error,
        readyState: audio.readyState,
        audioSrc: errorTarget?.src
      });

      // Check if this is a network/loading error vs a playback error
      const errorCode = error?.target?.error?.code;
      const errorMessage = error?.target?.error?.message || error?.message || '';
      const readyState = audio.readyState;

      // HTMLMediaElement readyState values:
      // HAVE_NOTHING (0) - No information available
      // HAVE_METADATA (1) - Metadata loaded
      // HAVE_CURRENT_DATA (2) - Data for current position available
      // HAVE_FUTURE_DATA (3) - Data for current and future positions available
      // HAVE_ENOUGH_DATA (4) - Enough data available to start playing

      // MEDIA_ERR_ABORTED (1) - User aborted, MEDIA_ERR_NETWORK (2) - Network error
      // MEDIA_ERR_DECODE (3) - Decode error, MEDIA_ERR_SRC_NOT_SUPPORTED (4) - Format not supported
      const isNetworkError = errorCode === 2;
      const isAbortError = errorCode === 1;
      const isDecodeError = errorCode === 3;
      const isUnsupportedError = errorCode === 4;

      // If we're still in early loading phase (HAVE_NOTHING or HAVE_METADATA), 
      // the error might be transient - wait a bit before showing error
      const isEarlyLoading = readyState === 0 || readyState === 1;
      const isCurrentlyLoading = isLoadingAudioRef.current;

      setIsLoadingAudio(false);
      isLoadingAudioRef.current = false;
      setIsPlayingAudio(false);
      isAudioPlayingRef.current = false;
      setPhonePlaying(false);
      markQuestionAudioInactive(isAbortError);

      // Clear the speech check interval
      if (audio.speechCheckInterval) {
        clearInterval(audio.speechCheckInterval);
        audio.speechCheckInterval = null;
        logger.debug('Cleared speech check interval on error');
      }

      // Clear the sound-off timeout if it exists
      if (soundOffTimeoutRef.current) {
        clearTimeout(soundOffTimeoutRef.current);
        soundOffTimeoutRef.current = null;
      }

      // Don't set error for abort errors (user-initiated or expected)
      if (isAbortError) {
        logger.debug('Audio playback aborted (this is expected in some cases)');
        // Only start recording if phone is still off-hook
        if (!isHungUpRef.current && phoneboxStateRef.current.isOffHook) {
          scheduleStartRecording();
        }
        return;
      }

      // If we're currently loading or in early loading phase, delay error reporting
      // Some browsers fire error events prematurely during loading - delay ALL error types
      if (isCurrentlyLoading || isEarlyLoading) {
        // Check if audio src is invalid (points to current page URL instead of audio file)
        const isInvalidSrc = audio.src && (audio.src === window.location.href || audio.src.endsWith('/'));
        const expectedAudioFile = questionsRef.current[currentQuestionIndexRef.current]?.audioFile;

        logger.debug('Error during loading phase, storing as pending and waiting to see if it resolves...', {
          errorCode,
          readyState,
          isCurrentlyLoading,
          isEarlyLoading,
          audioSrc: audio.src,
          currentUrl: window.location.href,
          isInvalidSrc,
          expectedAudioFile
        });

        // If src is invalid (points to current page), log warning
        if (isInvalidSrc && errorCode === 4) {
          logger.error('Audio source is invalid (points to current page URL) - expected:', expectedAudioFile);
        }
        // Store the error as pending (include all error types, not just network/unsupported)
        pendingErrorRef.current = {
          errorCode,
          isNetworkError,
          isUnsupportedError,
          isDecodeError,
          errorMessage,
          timestamp: Date.now()
        };
        // Use longer delay for unsupported errors (they're less likely to resolve, but give them more time)
        // Network errors might resolve quickly, so shorter delay
        const delayDuration = isUnsupportedError ? 2000 : 1500;
        // Wait to see if the error resolves (audio might still load)
        clearTimeoutRef(pendingErrorTimeoutRef);
        pendingErrorTimeoutRef.current = setTimeout(() => {
          // Don't show error if we're completing the sequence (hangup audio is separate)
          if (isCompletingRef.current) {
            logger.debug('Ignoring pending error timeout during hangup sequence');
            pendingErrorRef.current = null;
            pendingErrorTimeoutRef.current = null;
            return;
          }

          // Check if phone has been hung up - if so, don't process error
          if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
            logger.debug('Phone hung up during error delay - ignoring error');
            pendingErrorRef.current = null;
            pendingErrorTimeoutRef.current = null;
            return;
          }

          // Only show error if it's still pending and audio hasn't loaded/started playing
          // Check both the ref and the actual audio state (use refs only to avoid stale closures)
          const stillPending = pendingErrorRef.current &&
            pendingErrorRef.current.errorCode === errorCode;
          const audioNotReady = !isAudioPlayingRef.current && audio.readyState < 3;
          const stillLoading = isLoadingAudioRef.current; // Use ref only to avoid stale closure

          // CRITICAL: Also check if we're completing - don't hang up during hangup audio
          if (isCompletingRef.current) {
            logger.debug('Ignoring pending error timeout during hangup sequence (already checked above, but double-checking)');
            pendingErrorRef.current = null;
            return;
          }

          if (stillPending && audioNotReady && !stillLoading) {
            // Error persists and audio hasn't loaded - show error now
            logger.debug('Error persisted after delay - audio failed to load', {
              errorCode,
              readyState: audio.readyState,
              audioSrc: audio.src,
              isCompleting: isCompletingRef.current
            });
            let errorMsg = 'Failed to play question audio';
            if (pendingErrorRef.current.isNetworkError) {
              errorMsg = 'Failed to load question audio (network error)';
            } else if (pendingErrorRef.current.isUnsupportedError) {
              errorMsg = 'Question audio format not supported';
            } else if (pendingErrorRef.current.isDecodeError) {
              errorMsg = 'Failed to decode question audio';
            } else if (pendingErrorRef.current.errorMessage) {
              errorMsg = `Failed to play question audio: ${pendingErrorRef.current.errorMessage}`;
            }
            setError(errorMsg);
            // Automatically hang up on error - but NOT if we're completing
            if (!isHungUpRef.current && !isCompletingRef.current) {
              logger.debug('Error occurred - automatically hanging up phone');
              hangup({ reason: 'error' });
            } else if (isCompletingRef.current) {
              logger.debug('Error occurred but we are completing - ignoring hangup');
            }
            pendingErrorRef.current = null;
          } else {
            // Error resolved or audio loaded - don't show error
            logger.debug('Error resolved during delay or audio loaded successfully', {
              stillPending,
              audioNotReady,
              stillLoading,
              readyState: audio.readyState,
              isAudioPlaying: isAudioPlayingRef.current
            });
            pendingErrorRef.current = null;
          }
          pendingErrorTimeoutRef.current = null;
        }, delayDuration);
        // Don't set error immediately, wait for the timeout
        return;
      }

      // Only set error for actual playback failures (not during loading)
      // At this point, we're NOT in loading phase, so errors are real
      // BUT: Don't hang up if we're completing (hangup audio is playing)
      if (isNetworkError || isDecodeError || isUnsupportedError || errorMessage) {
        let errorMsg = 'Failed to play question audio';
        if (isNetworkError) {
          errorMsg = 'Failed to load question audio (network error)';
        } else if (isDecodeError) {
          errorMsg = 'Failed to decode question audio';
        } else if (isUnsupportedError) {
          errorMsg = 'Question audio format not supported';
        } else if (errorMessage) {
          errorMsg = `Failed to play question audio: ${errorMessage}`;
        }
        setError(errorMsg);
        // Automatically hang up on error - but NOT if we're completing
        if (!isHungUpRef.current && !isCompletingRef.current) {
          logger.debug('Error occurred - automatically hanging up phone');
          hangup({ reason: 'error' });
        } else if (isCompletingRef.current) {
          logger.debug('Error occurred but we are completing - ignoring hangup');
        }
      } else {
        // Unknown error - log but don't show to user unless it's persistent
        logger.warn('Unknown audio error occurred:', error);
        // Still hang up on unknown errors to prevent stuck state - but NOT if we're completing
        if (!isHungUpRef.current && phoneboxStateRef.current.isOffHook && !isCompletingRef.current) {
          logger.debug('Unknown error occurred - automatically hanging up phone');
          hangup({ reason: 'error' });
        } else if (isCompletingRef.current) {
          logger.debug('Unknown error occurred but we are completing - ignoring hangup');
        }
      }
    };

    // Add event listeners using named functions
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    logger.audio('Single audio player initialized');
  }, [setPhonePlaying, forceStopSpeechRecognition, markQuestionAudioInactive, phoneboxState.audioContext, phoneboxState.analyser, isSoundOn, scheduleStartRecording, hangup, isAndroidChrome, isPlayingAudio, speechRecognitionSupported]);

  // Initialize speech recognition
  const initializeSpeechRecognition = useCallback(() => {
    // Don't re-initialize if already initialized
    if (speechRecognitionInitializedRef.current && speechRecognitionRef.current) {
      logger.debug('Speech recognition already initialized, skipping re-initialization', { isAndroidChrome });
      setSpeechRecognitionSupported(true);
      return true;
    }

    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      logger.warn('Speech recognition not supported in this browser');
      setSpeechRecognitionSupported(false);
      return false;
    }

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      // Android Chrome workaround: Use continuous=false to avoid immediate ending bug
      // Android Chrome has a known bug where continuous=true causes speech recognition
      // to end immediately. Using continuous=false and manually restarting is more reliable.
      // NOTE: Android Chrome may not fire onresult events at all - this is a known limitation.
      // We check for results in onend as a fallback, but client-side transcription may not work.
      // Audio is still recorded and uploaded for server-side transcription.
      recognition.continuous = !isAndroidChrome; // false on Android, true on other platforms
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      // Android Chrome specific configuration
      if (isAndroidChrome) {
        // Android Chrome workaround: Use continuous=false to prevent immediate ending bug
        // We'll manually restart in onend handler to maintain continuous operation
        // NOTE: onresult may not fire on Android Chrome - results may only be available in onend
        // or not at all. We rely on server-side transcription of recorded audio.
        logger.debug('Android Chrome detected - using continuous=false workaround to prevent immediate ending bug');
        logger.warn('Android Chrome: Client-side transcription may not work - relying on server-side transcription');
      }

      recognition.onstart = () => {
        logger.debug('Speech recognition started', {
          isAndroidChrome,
          restartCount: speechRecognitionRestartCountRef.current
        });
        isSpeechRecognitionActiveRef.current = true;
        speechRecognitionWasActiveRef.current = true; // Mark that speech recognition was active during this recording
        // Only update state if it's not already true to avoid unnecessary rerenders
        // This is especially important on Android where speech recognition restarts frequently
        if (!isTranscribingRef.current) {
          setIsTranscribing(true);
          isTranscribingRef.current = true;
        }
        // Reset restart count on successful start
        speechRecognitionRestartCountRef.current = 0;
        logger.debug('Speech recognition onstart: Set isTranscribing to true', {
          isAndroidChrome,
          isActive: isSpeechRecognitionActiveRef.current,
          wasAlreadyTranscribing: isTranscribingRef.current
        });
      };

      recognition.onresult = (event) => {
        // Log that we received a result (for debugging Android speech detection)
        // ANDROID: With continuous=false, we might get different result structures
        logger.debug('Speech recognition onresult fired', {
          isAndroidChrome,
          resultIndex: event.resultIndex,
          resultsLength: event.results.length,
          isFinal: event.results[event.resultIndex]?.isFinal,
          // Log all results for Android debugging
          allResults: isAndroidChrome ? Array.from(event.results).map((r, idx) => ({
            index: idx,
            isFinal: r.isFinal,
            transcript: r[0]?.transcript || '',
            confidence: r[0]?.confidence || 0
          })) : undefined
        });

        // CRITICAL: Don't process speech results if we're completing the sequence (hangup audio playing)
        // This prevents transcriptions from appearing during hangup audio
        if (isCompletingRef.current) {
          logger.debug('Ignoring speech recognition result - completion sequence in progress (hangup audio playing)');
          return;
        }

        // Don't process speech results if phone is hung up - forcefully reset phone and stop listening
        if (isHungUpRef.current || !phoneboxStateRef.current.isOffHook) {
          logger.debug('Ignoring speech recognition result - phone is hung up, forcing complete reset');
          // Forcefully stop speech recognition to prevent further callbacks
          try {
            recognition.stop();
            isSpeechRecognitionActiveRef.current = false;
            setIsTranscribing(false);
            isTranscribingRef.current = false;
          } catch (error) {
            console.warn('Error stopping speech recognition after hangup:', error);
          }
          // Call the complete hangup reset function to reset phone and stop listening
          if (handleHangupResetRef.current) {
            handleHangupResetRef.current();
          }
          return;
        }

        // Don't process speech results if audio is playing
        if (isPlayingAudio || isAudioPlayingRef.current) {
          logger.debug('Ignoring speech recognition result - audio is playing');
          return;
        }

        // Don't process speech results during cooldown period after audio ends
        // This prevents detecting question audio as user speech
        const currentTime = Date.now();
        if (audioEndedTimeRef.current && (currentTime - audioEndedTimeRef.current) < SPEECH_RECOGNITION_COOLDOWN_MS) {
          const timeSinceAudioEnded = currentTime - audioEndedTimeRef.current;
          logger.debug(`Ignoring speech recognition result - within cooldown period after audio ended (${timeSinceAudioEnded}ms ago)`);
          return;
        }

        // Check if we have valid speech with sufficient confidence
        let hasValidSpeech = false;
        let speechDuration = 0;
        let latestTranscript = '';
        let latestConfidence = 0;

        // ANDROID: With continuous=false, we might need to check all results, not just from resultIndex
        // Some Android Chrome versions may structure results differently
        // Start from resultIndex for normal behavior, but on Android also check all results if needed
        const startIndex = isAndroidChrome ? 0 : event.resultIndex;
        for (let i = startIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const confidence = result[0]?.confidence || 0;
          const transcript = result[0]?.transcript || '';
          const trimmedTranscript = transcript.trim();

          if (result.isFinal && trimmedTranscript.length > 0 && isRepeatRequest(trimmedTranscript)) {
            handleRepeatRequest(trimmedTranscript);
            return;
          }

          // Update confidence display
          setCurrentSpeechConfidence(confidence);

          // Always update the latest transcript for debugging
          if (trimmedTranscript.length > 0) {
            latestTranscript = transcript;
            latestConfidence = confidence;
            setDetectedSpeech(transcript);
            logger.debug('Updated detected speech:', transcript, 'confidence:', confidence);
          }

          // Only consider speech valid if it has sufficient confidence and content
          if (confidence >= SPEECH_CONFIDENCE_THRESHOLD && transcript.trim().length > 0) {
            hasValidSpeech = true;

            // Clear no-speech timeout since we detected speech
            if (noSpeechTimeoutRef.current) {
              clearTimeout(noSpeechTimeoutRef.current);
              noSpeechTimeoutRef.current = null;
              logger.debug('Cleared no-speech timeout - speech detected');
            }

            // Check if this result (interim or final) meets the confirmation threshold (70%)
            if (confidence >= SPEECH_CONFIRMATION_THRESHOLD && transcript.trim().length > 0) {
              hasConfirmedAnswerRef.current = true;
              logger.debug(`Answer confirmed with ${(confidence * 100).toFixed(0)}% confidence (${result.isFinal ? 'final' : 'interim'}):`, transcript);
              console.log('[DEBUG] Speech recognition - Answer confirmed:', {
                confidence: (confidence * 100).toFixed(0) + '%',
                isFinal: result.isFinal,
                transcript: transcript.trim(),
                shouldReplay: shouldReplayQuestionRef.current,
                hasValidAnswerRef: hasValidAnswerRef.current
              });
            }

            if (result.isFinal) {
              logger.debug(`Final speech result (confidence: ${confidence.toFixed(2)}):`, transcript);

              // Store the confirmed transcription immediately
              const currentQuestionId = questionsRef.current[currentQuestionIndexRef.current]?.id;
              if (currentQuestionId && transcript.trim().length > 0) {
                setConfirmedTranscriptions(prev => ({
                  ...prev,
                  [currentQuestionId]: transcript.trim()
                }));
                logger.debug(`Stored confirmed transcription for ${currentQuestionId}:`, transcript.trim());
              }

              // If we have a final result with high confidence, stop recording immediately
              if (confidence >= 0.8 && transcript.trim().length > 2) {
                logger.debug('High confidence final result detected, stopping recording');

                // Stop speech recognition immediately
                if (speechRecognitionRef.current && isSpeechRecognitionActiveRef.current) {
                  try {
                    speechRecognitionRef.current.stop();
                    isSpeechRecognitionActiveRef.current = false;
                    logger.debug('Stopped speech recognition due to high confidence final result');
                  } catch (error) {
                    console.error('Error stopping speech recognition:', error);
                  }
                }

                // Stop recording immediately (don't schedule - we have high confidence answer)
                // Use a small delay to ensure speech recognition has fully stopped
                setTimeout(() => {
                  if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    stopRecording();
                  } else {
                    logger.debug('Recording already stopped when high confidence timeout fired');
                  }
                }, 100); // Small delay to ensure speech recognition stops cleanly
              }
            }
          } else if (transcript.trim().length > 0) {
            // Log when we have speech but it doesn't meet the confidence threshold
            console.log('[DEBUG] Speech recognition - Speech detected but below confidence threshold:', {
              confidence: (confidence * 100).toFixed(0) + '%',
              threshold: (SPEECH_CONFIDENCE_THRESHOLD * 100).toFixed(0) + '%',
              transcript: transcript.trim(),
              isFinal: result.isFinal
            });
          }
        }

        // Update debug info
        setSpeechDebugInfo(`Latest: "${latestTranscript}" (${(latestConfidence * 100).toFixed(0)}% confidence, valid: ${hasValidSpeech})`);

        // Update speech timing for any detected speech (not just valid speech)
        // Only do this if speech recognition is still active
        if (latestTranscript.trim().length > 0 && isSpeechRecognitionActiveRef.current) {
          // Track when speech started
          if (speechStartTimeRef.current === null) {
            speechStartTimeRef.current = currentTime;
            logger.debug('Speech started');
          }

          lastSpeechTimeRef.current = currentTime;

          // Only mark as valid speech if it meets confidence threshold
          if (hasValidSpeech) {
            if (!hasValidSpeechRef.current) {
              console.log('[DEBUG] Speech recognition - Setting hasValidSpeechRef to true:', {
                confidence: (latestConfidence * 100).toFixed(0) + '%',
                transcript: latestTranscript.trim()
              });
            }
            hasValidSpeechRef.current = true;
          }

          // Calculate speech duration
          speechDuration = currentTime - speechStartTimeRef.current;

          // Clear existing speech end timeout
          if (speechEndTimeoutRef.current) {
            clearTimeout(speechEndTimeoutRef.current);
            setSpeechEndTimeoutActive(false);
          }

          // Set timeout for any speech (no minimum duration required)
          if (latestTranscript.trim().length > 0) {
            // Clear existing timeout first
            if (speechEndTimeoutRef.current) {
              clearTimeout(speechEndTimeoutRef.current);
              speechEndTimeoutRef.current = null;
              setSpeechEndTimeoutActive(false);
            }

            // Determine timeout duration based on speech validity and duration
            let timeoutDuration = SPEECH_END_TIMEOUT;
            if (hasValidSpeech && speechDuration >= 2000) {
              timeoutDuration = 500; // Much shorter timeout for valid speech
              logger.debug(`Valid speech detected for ${speechDuration}ms, setting shorter timeout`);
            }

            speechEndTimeoutRef.current = setTimeout(() => {
              if (isRecording && isSpeechRecognitionActiveRef.current) {
                logger.debug(`Speech end timeout triggered after ${speechDuration}ms - stopping recording`);
                setSpeechEndTimeoutActive(false);
                stopRecording();
              }
            }, timeoutDuration);

            setSpeechEndTimeoutActive(true);
            logger.debug(`Set speech end timeout for ${timeoutDuration}ms (speech duration: ${speechDuration}ms, valid: ${hasValidSpeech})`);
          }
        }
      };

      recognition.onerror = (event) => {
        const errorType = event.error;
        console.error('Speech recognition error:', errorType);
        logger.error('Speech recognition error:', errorType, { isAndroidChrome });
        console.log('[DEBUG] Speech recognition error - State:', {
          error: errorType,
          isActive: isSpeechRecognitionActiveRef.current,
          shouldReplay: shouldReplayQuestionRef.current,
          hasConfirmedAnswer: hasConfirmedAnswerRef.current,
          isRecording,
          isAndroidChrome,
          phoneOffHook: phoneboxStateRef.current.isOffHook,
          hasRecognition: !!speechRecognitionRef.current
        });

        isSpeechRecognitionActiveRef.current = false;
        setIsTranscribing(false);
        isTranscribingRef.current = false;

        // Android Chrome specific error handling
        if (isAndroidChrome) {
          // Android Chrome may throw "not-allowed" if not started from user gesture
          // or "service-not-allowed" if speech recognition service is unavailable
          if (errorType === 'not-allowed' || errorType === 'service-not-allowed') {
            logger.warn('Android Chrome speech recognition blocked:', errorType, '- This may require user gesture to start');
            // Don't try to restart automatically for these errors on Android
            return;
          }

          // Android Chrome may throw "aborted" more frequently
          // Use shorter retry delay for Android
          if (errorType === 'aborted' && isRecording && !isPlayingAudio && !isAudioPlayingRef.current && !isCompletingRef.current) {
            logger.debug('Android Chrome: Speech recognition aborted - attempting to restart with shorter delay');
            // Shorter delay for Android Chrome
            setTimeout(() => {
              if (isRecording &&
                !isPlayingAudio &&
                !isAudioPlayingRef.current &&
                !isQuestionAudioActiveRef.current &&
                !isCompletingRef.current &&
                phoneboxStateRef.current.isOffHook &&
                speechRecognitionRef.current &&
                !isSpeechRecognitionActiveRef.current) {
                try {
                  if (isCompletingRef.current) {
                    logger.debug('Aborting speech recognition restart - completion sequence in progress');
                    return;
                  }
                  logger.debug('Android Chrome: Restarting speech recognition after aborted error');
                  speechRecognitionRef.current.start();
                } catch (restartError) {
                  logger.debug('Android Chrome: Failed to restart speech recognition:', restartError);
                }
              }
            }, 100); // Shorter delay for Android
            return;
          }
        }

        // Handle "aborted" errors - these are often transient and shouldn't cause replay
        // Try to restart speech recognition if we're still recording
        // CRITICAL: Don't restart if we're completing the sequence (hangup audio playing)
        if (errorType === 'aborted' && isRecording && !isPlayingAudio && !isAudioPlayingRef.current && !isCompletingRef.current) {
          logger.debug('Speech recognition aborted during recording - attempting to restart');
          // Small delay before restarting to avoid immediate re-abort
          setTimeout(() => {
            // Check conditions again before restarting (including completion state)
            if (isRecording &&
              !isPlayingAudio &&
              !isAudioPlayingRef.current &&
              !isQuestionAudioActiveRef.current &&
              !isCompletingRef.current && // CRITICAL: Don't restart during completion
              phoneboxStateRef.current.isOffHook &&
              speechRecognitionRef.current &&
              !isSpeechRecognitionActiveRef.current) {
              try {
                // Final safety check before starting
                if (isCompletingRef.current) {
                  logger.debug('Aborting speech recognition restart - completion sequence in progress');
                  return;
                }
                logger.debug('Restarting speech recognition after aborted error');
                speechRecognitionRef.current.start();
              } catch (restartError) {
                // If restart fails, log but don't treat as fatal - recording can continue without speech recognition
                logger.debug('Failed to restart speech recognition after aborted error:', restartError);
              }
            }
          }, 300);
        }

        // Handle "no-speech" errors - if user hasn't spoken after a reasonable time, stop recording
        if (errorType === 'no-speech' && isRecording) {
          logger.debug('No speech detected - setting timeout to stop recording if no speech occurs');
          // Clear any existing no-speech timeout
          if (noSpeechTimeoutRef.current) {
            clearTimeout(noSpeechTimeoutRef.current);
            noSpeechTimeoutRef.current = null;
          }
          // Get the current question's maxDuration for the no-speech timeout
          const currentQuestion = questionsRef.current[currentQuestionIndexRef.current];
          const noSpeechTimeout = currentQuestion?.maxDuration || 7000; // Fallback to 7 seconds if question not found
          // Set timeout to stop recording if no speech is detected
          noSpeechTimeoutRef.current = setTimeout(() => {
            // Check if we still haven't detected any speech
            if (isRecording && !hasValidSpeechRef.current && !hasConfirmedAnswerRef.current) {
              logger.debug('No speech timeout reached - stopping recording (user has not answered)');
              stopRecording();
            }
            noSpeechTimeoutRef.current = null;
          }, noSpeechTimeout);
        }

        // For other errors (network, etc.), don't restart automatically
        // These are more serious and might indicate real issues
        if (errorType !== 'aborted' && errorType !== 'no-speech') {
          logger.warn('Speech recognition error (non-aborted):', errorType);
        }
      };

      recognition.onend = () => {
        // ANDROID: Check if stream has active tracks
        const streamHasActiveTracksForLog = stream && stream.getAudioTracks && stream.getAudioTracks().some(track => track.readyState === 'live');

        // ANDROID: With continuous=false, results might only be available in onend, not onresult
        // Try to access results from the recognition object if onresult didn't fire
        let androidResults = null;
        if (isAndroidChrome && recognition.results && recognition.results.length > 0) {
          androidResults = Array.from(recognition.results).map((r, idx) => ({
            index: idx,
            isFinal: r.isFinal,
            transcript: r[0]?.transcript || '',
            confidence: r[0]?.confidence || 0
          }));
          logger.debug('Android: Found results in onend (onresult may not have fired)', {
            resultsCount: recognition.results.length,
            results: androidResults
          });

          // Process Android results if we have them and onresult didn't fire
          // We'll process them similar to how onresult processes them
          if (androidResults.length > 0 && isRecording && !isCompletingRef.current && phoneboxStateRef.current.isOffHook) {
            // Process the results - find the best final result
            const finalResults = androidResults.filter(r => r.isFinal && r.transcript.trim().length > 0);
            if (finalResults.length > 0) {
              // Use the last final result (most recent)
              const bestResult = finalResults[finalResults.length - 1];
              logger.debug('Android: Processing final result from onend', bestResult);

              if (bestResult.confidence >= SPEECH_CONFIDENCE_THRESHOLD) {
                // Update detected speech
                setDetectedSpeech(bestResult.transcript);
                setCurrentSpeechConfidence(bestResult.confidence);

                // Store confirmed transcription
                const currentQuestionId = questionsRef.current[currentQuestionIndexRef.current]?.id;
                if (currentQuestionId) {
                  setConfirmedTranscriptions(prev => ({
                    ...prev,
                    [currentQuestionId]: bestResult.transcript.trim()
                  }));
                  logger.debug(`Android: Stored transcription from onend for ${currentQuestionId}:`, bestResult.transcript.trim());
                }

                // Mark as valid speech
                hasValidSpeechRef.current = true;
                if (bestResult.confidence >= SPEECH_CONFIRMATION_THRESHOLD) {
                  hasConfirmedAnswerRef.current = true;
                }

                // Clear no-speech timeout
                if (noSpeechTimeoutRef.current) {
                  clearTimeout(noSpeechTimeoutRef.current);
                  noSpeechTimeoutRef.current = null;
                }
              }
            }
          }
        }

        logger.debug('Speech recognition ended', {
          isAndroidChrome,
          isRecording,
          isCompleting: isCompletingRef.current,
          phoneOffHook: phoneboxStateRef.current.isOffHook,
          hasRecognition: !!speechRecognitionRef.current,
          wasActive: isSpeechRecognitionActiveRef.current,
          hasStream: !!stream,
          hasActiveTracks: streamHasActiveTracksForLog,
          trackCount: stream?.getAudioTracks?.()?.length || 0,
          androidResultsFound: !!androidResults,
          androidResultsCount: androidResults?.length || 0
        });
        isSpeechRecognitionActiveRef.current = false;

        // CRITICAL: Don't restart speech recognition if we're completing the sequence (hangup audio playing)
        // This prevents speech recognition from restarting during hangup audio
        if (isCompletingRef.current) {
          logger.debug('Speech recognition ended during completion - not restarting');
          setIsTranscribing(false);
          isTranscribingRef.current = false;
          return;
        }

        // ANDROID: Keep speech recognition running continuously if phone is off-hook
        // This is critical for Android Chrome which requires continuous operation
        // With continuous=false, speech recognition ends after each utterance (or immediately if no speech detected)
        // We MUST restart it whenever it ends, as long as phone is off-hook
        // The stream check is very lenient because with continuous=false, speech recognition may end
        // before the stream is fully initialized, but we still want to restart
        // If phone is off-hook, the stream should exist (or will exist soon), so we restart regardless
        const shouldRestartOnAndroid = isAndroidChrome &&
          phoneboxStateRef.current.isOffHook &&
          !isCompletingRef.current &&
          speechRecognitionRef.current;
        // ANDROID: Don't check stream state - if phone is off-hook, restart regardless
        // This handles the case where speech recognition ends immediately before stream is ready

        // If speech recognition ended naturally during recording (not stopped manually),
        // try to restart it to continue capturing speech
        // This handles cases where recognition stops unexpectedly but recording continues
        const shouldRestart = (isRecording &&
          !isPlayingAudio &&
          !isAudioPlayingRef.current &&
          !isQuestionAudioActiveRef.current &&
          phoneboxStateRef.current.isOffHook &&
          speechRecognitionRef.current) || shouldRestartOnAndroid;

        if (shouldRestart) {
          // ANDROID: With continuous=false, speech recognition ends after each utterance or timeout
          // We need to restart it quickly but with a small delay to let it properly end first
          // Too short (0ms) can cause restart failures, too long (500ms) misses speech
          // 50ms is a good balance - fast enough to catch speech, slow enough to avoid conflicts
          const restartDelay = isAndroidChrome ? 50 : 200;

          // ANDROID: Keep isTranscribing true if we're restarting
          // This prevents UI flickering and ensures continuous transcription
          if (!isAndroidChrome) {
            setIsTranscribing(false);
            isTranscribingRef.current = false;
          } else {
            logger.debug('Android: Speech recognition ended but will restart - keeping isTranscribing true', { delay: restartDelay });
          }

          setTimeout(() => {
            // Check conditions again before restarting (including completion state)
            // ANDROID: Restart as long as phone is off-hook (same logic as initial check)
            // Re-evaluate isAndroidChrome in case of closure issues
            const currentIsAndroidChrome = typeof window !== 'undefined' &&
              /android/i.test(navigator.userAgent) &&
              /chrome/i.test(navigator.userAgent) &&
              !/firefox/i.test(navigator.userAgent);
            const stillShouldRestart = (isRecording &&
              !isPlayingAudio &&
              !isAudioPlayingRef.current &&
              !isQuestionAudioActiveRef.current &&
              !isCompletingRef.current && // CRITICAL: Don't restart during completion
              phoneboxStateRef.current.isOffHook &&
              speechRecognitionRef.current &&
              !isSpeechRecognitionActiveRef.current) ||
              (currentIsAndroidChrome &&
                phoneboxStateRef.current.isOffHook &&
                !isCompletingRef.current &&
                speechRecognitionRef.current &&
                !isSpeechRecognitionActiveRef.current); // ANDROID: Restart if phone is off-hook (no stream check needed)

            if (stillShouldRestart) {
              try {
                // Final safety check before starting
                if (isCompletingRef.current) {
                  logger.debug('Aborting speech recognition restart - completion sequence in progress');
                  if (!isAndroidChrome) {
                    setIsTranscribing(false);
                    isTranscribingRef.current = false;
                  }
                  return;
                }
                speechRecognitionLastRestartTimeRef.current = Date.now();
                logger.debug('Speech recognition ended naturally - restarting', {
                  isAndroidChrome,
                  delay: restartDelay,
                  restartCount: speechRecognitionRestartCountRef.current,
                  hasLiveStream,
                  permissionState
                });
                speechRecognitionRef.current.start();
                // Note: isTranscribing will be set to true by onstart callback
                // On Android, it should already be true from above, but onstart will confirm
              } catch (restartError) {
                // If restart fails, log but don't treat as fatal
                logger.warn('Failed to restart speech recognition after onend:', restartError);
                setIsTranscribing(false);
                isTranscribingRef.current = false;
              }
            } else {
              // Conditions changed - won't restart
              // Log detailed info to diagnose why restart isn't happening
              const streamHasActiveTracks = stream && stream.getAudioTracks && stream.getAudioTracks().some(track => track.readyState === 'live');
              logger.debug('Speech recognition restart conditions no longer met', {
                isAndroidChrome,
                isRecording,
                isCompleting: isCompletingRef.current,
                phoneOffHook: phoneboxStateRef.current.isOffHook,
                hasRecognition: !!speechRecognitionRef.current,
                isActive: isSpeechRecognitionActiveRef.current,
                isPlayingAudio,
                isAudioPlaying: isAudioPlayingRef.current,
                isQuestionAudioActive: isQuestionAudioActiveRef.current,
                hasStream: !!stream,
                streamHasActiveTracks,
                // Check each part of the condition
                androidCondition: isAndroidChrome &&
                  phoneboxStateRef.current.isOffHook &&
                  !isCompletingRef.current &&
                  speechRecognitionRef.current &&
                  !isSpeechRecognitionActiveRef.current
              });
              setIsTranscribing(false);
              isTranscribingRef.current = false;
            }
          }, restartDelay);
        } else {
          // Won't restart - set isTranscribing false
          // Log detailed info for Android to diagnose why restart isn't happening
          const debugInfo = {
            isAndroidChrome,
            isRecording,
            phoneOffHook: phoneboxStateRef.current.isOffHook,
            hasRecognition: !!speechRecognitionRef.current,
            isCompleting: isCompletingRef.current,
            hasStream: !!stream,
            streamHasActiveTracks: streamHasActiveTracksForLog,
            shouldRestartOnAndroid: isAndroidChrome &&
              phoneboxStateRef.current.isOffHook &&
              !isCompletingRef.current &&
              speechRecognitionRef.current
          };
          logger.debug('Speech recognition ended and will not restart', debugInfo);
          setIsTranscribing(false);
          isTranscribingRef.current = false;
        }
      };

      speechRecognitionRef.current = recognition;
      speechRecognitionInitializedRef.current = true; // Mark as initialized
      setSpeechRecognitionSupported(true);

      if (isAndroidChrome) {
        logger.debug('Speech recognition initialized for Android Chrome');
      }

      return true;
    } catch (error) {
      console.error('Failed to initialize speech recognition:', error);
      setSpeechRecognitionSupported(false);
      return false;
    }
  }, [isRecording, stopRecording, isPlayingAudio, handleRepeatRequest, isRepeatRequest, isAndroidChrome, hasLiveStream, permissionState, stream]);

  // Start speech recognition
  const startSpeechRecognition = useCallback(() => {
    // CRITICAL: Don't start speech recognition if we're completing the sequence (hangup audio playing)
    if (isCompletingRef.current) {
      logger.debug('Not starting speech recognition - completing sequence (hangup audio playing)');
      return;
    }

    // Check if audio is playing - if so, don't start speech recognition
    const audioIsPlaying = isPlayingAudio ||
      isAudioPlayingRef.current ||
      isQuestionAudioActiveRef.current ||
      (questionAudioRef.current && !questionAudioRef.current.paused);

    if (!speechRecognitionRef.current ||
      isSpeechRecognitionActiveRef.current ||
      audioIsPlaying) {
      logger.debug('Not starting speech recognition:', {
        hasRecognition: !!speechRecognitionRef.current,
        isActive: isSpeechRecognitionActiveRef.current,
        isPlayingAudio,
        isAudioPlayingRef: isAudioPlayingRef.current,
        isQuestionAudioActive: isQuestionAudioActiveRef.current,
        audioPaused: questionAudioRef.current ? questionAudioRef.current.paused : 'no audio ref',
        audioIsPlaying,
        isCompleting: isCompletingRef.current,
        isAndroidChrome
      });
      return;
    }

    try {
      // Final safety check before starting (state may have changed)
      if (isCompletingRef.current) {
        logger.debug('Aborting speech recognition start - completion sequence in progress');
        return;
      }

      // Reset speech tracking variables (some may already be reset in startRecording, but ensure they're clean)
      // Note: These are reset in startRecording() but we reset here too for safety if startSpeechRecognition is called independently
      lastSpeechTimeRef.current = null;
      speechStartTimeRef.current = null;
      hasValidSpeechRef.current = false;
      hasConfirmedAnswerRef.current = false; // Reset confirmed answer flag

      // Mark that we're attempting to start speech recognition (will be confirmed in onstart)
      // This helps track activity even if recognition aborts immediately
      speechRecognitionWasActiveRef.current = true;

      // Android Chrome workaround: Try to start immediately and synchronously
      // Android Chrome requires speech recognition to start from user gesture context
      // Since we're called from setTimeout, Android Chrome may block this
      // We'll try to start it anyway and handle errors gracefully
      if (isAndroidChrome) {
        logger.debug('Android Chrome: Attempting to start speech recognition (may be blocked if not from user gesture)');
        // Try starting immediately - Android Chrome may block if not from user gesture
        // The error handler will catch this and prevent retries
        speechRecognitionRef.current.start();
      } else {
        // Non-Android: Start normally
        speechRecognitionRef.current.start();
      }

      // Note: speechRecognitionWasActiveRef is also set to true in onstart handler as confirmation
      logger.debug('Started speech recognition for speech end detection (balanced mode)');
      logger.debug('Speech recognition state:', {
        isActive: isSpeechRecognitionActiveRef.current,
        isPlayingAudio,
        isAudioPlayingRef: isAudioPlayingRef.current,
        isAndroidChrome
      });
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      // On Android Chrome, if this fails due to user gesture requirement,
      // log it but don't treat as fatal - MediaRecorder will still work
      if (isAndroidChrome) {
        logger.warn('Android Chrome: Speech recognition start failed - recording will continue without speech recognition:', error.message || error);
      }
    }
  }, [isPlayingAudio, isAndroidChrome]);

  // Stop speech recognition
  // Update refs when values change
  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  useEffect(() => {
    currentQuestionIndexRef.current = currentQuestionIndex;
  }, [currentQuestionIndex]);

  useEffect(() => {
    phoneboxStateRef.current = phoneboxState;
  }, [phoneboxState]);

  // Reset the question sequence
  const resetQuestionSequence = useCallback(() => {
    logger.debug('Resetting question sequence');

    // Stop any active recording
    if (isRecording) {
      stopRecording();
    }

    // Stop speech recognition (handled by stopRecording)

    // Reset all state
    setCurrentQuestionIndex(0);
    setIsRecording(false);
    setLocalQuestionState('idle');
    setResponses([]);
    setError(null);
    setIsPlayingAudio(false);
    setCurrentSpeechConfidence(0);
    setConfirmedTranscriptions({});
    setIsCompleting(false);
    isCompletingRef.current = false;
    setSubmittedQuestions(new Set());
    setIsProcessingResponse(false);
    setCurrentSubtitleText(null); // Clear subtitle text on reset
    setCurrentSubtitleTimeouts(null); // Clear subtitle timeouts on reset

    // Generate new submission ID for the next session
    const generateSubmissionId = () => {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      return `submission_${timestamp}_${randomId}`;
    };
    const newSubmissionId = generateSubmissionId();
    submissionIdRef.current = newSubmissionId;
    setSubmissionId(newSubmissionId);
    sessionStorage.setItem('questionSequenceSubmissionId', newSubmissionId);
    logger.debug('Reset: Generated new submission ID for next session:', newSubmissionId);

    // Reset refs
    lastSpeechTimeRef.current = null;
    speechStartTimeRef.current = null;
    hasValidSpeechRef.current = false;
    hasConfirmedAnswerRef.current = false; // Reset confirmed answer flag
    speechRecognitionWasActiveRef.current = false; // Reset speech recognition tracking
    speechRecognitionStartedEarlyRef.current = false; // Reset early start flag
    audioEndedTimeRef.current = null; // Reset audio ended time tracking
    shouldReplayQuestionRef.current = false; // Reset replay flag
    hasValidAnswerRef.current = false; // Reset valid answer flag

    // Clear all timeouts
    clearAllTimeouts();

    // Reset media recorder initialization flag
    mediaRecorderInitializedRef.current = false;
    firstQuestionStartedRef.current = false;
    currentQuestionTextRef.current = null; // Clear question text ref
    currentQuestionTimeoutsRef.current = null; // Clear question timeouts ref

    // Reset audio player state
    if (questionAudioRef.current) {
      try {
        questionAudioRef.current.pause();
        questionAudioRef.current.currentTime = 0;
        questionAudioRef.current.src = '';
      } catch (error) {
        console.warn('Error resetting audio player:', error);
      }
    }

    // IMPORTANT: Ensure soundwaves are hidden when resetting
    setPhonePlaying(false);

    // Clear hangup audio ref (but don't stop it if it's playing - that's handled elsewhere)
    // Don't clear if we're completing the sequence - let processHangup handle it
    if (!isCompletingRef.current) {
      hangupAudioRef.current = null;
    }

    // Clear hangup timeout (but don't clear if we're completing - let processHangup handle it)
    if (!isCompletingRef.current && hangupTimeoutRef.current) {
      clearTimeout(hangupTimeoutRef.current);
      hangupTimeoutRef.current = null;
    }

    // Clear pending uploads tracking
    pendingUploadsRef.current.clear();

    // Reset upload state
    setIsUploading(false);
    setUploadError(null);

    isAudioPlayingRef.current = false;

    logger.debug('Question sequence reset complete');
  }, [isRecording, stopRecording, clearAllTimeouts, setPhonePlaying]);

  // Complete the question sequence
  const completeQuestionSequence = useCallback(() => {
    // Prevent duplicate completion calls
    if (isCompleting) {
      logger.debug('Question sequence completion already in progress, skipping duplicate call');
      return;
    }

    setIsCompleting(true);
    isCompletingRef.current = true;
    logger.debug('Question sequence completed. Responses:', responses);
    trackQuestionEvent('question_sequence_completed', {
      question_count: responses.length
    });

    // CRITICAL: Stop speech recognition immediately when completing sequence
    // This prevents speech recognition from continuing during hangup audio
    // ANDROID: Also stop here since we're completing (hangup audio will play)
    forceStopSpeechRecognition();
    logger.debug('Stopped speech recognition at start of completion sequence', { isAndroidChrome });

    // Clear any existing error state and pending errors BEFORE clearing question audio src
    // This prevents errors from firing when we clear the src
    setError(null);
    clearTimeoutRef(pendingErrorTimeoutRef);
    pendingErrorRef.current = null;

    // Also clear isLoadingAudio flag to prevent error handler from thinking we're loading
    setIsLoadingAudio(false);
    isLoadingAudioRef.current = false;

    // Stop and clear question audio before playing hangup audio
    // IMPORTANT: isCompletingRef is already set above, so question audio handlers will ignore events
    // IMPORTANT: Pending errors are cleared above, so any new errors won't trigger hangup
    if (questionAudioRef.current) {
      try {
        questionAudioRef.current.pause();
        questionAudioRef.current.currentTime = 0;
        // Clear src - this will trigger loadstart/error events, but handlers will check isCompletingRef and ignore them
        // Also, pending errors are already cleared above, so delayed error timeouts won't fire
        questionAudioRef.current.src = '';
        isAudioPlayingRef.current = false;
        setIsPlayingAudio(false);
      } catch (error) {
        console.warn('Error stopping question audio before hangup:', error);
      }
    }
    markQuestionAudioInactive(true);

    // Set hangup subtitle text and timeouts
    setCurrentSubtitleText(HANGUP_CONFIG.text);
    setCurrentSubtitleTimeouts(HANGUP_CONFIG.subtitleTimeouts);

    // Reuse the same Audio object that was used for phonebox.mp3
    // This ensures we use the same audio element and maintain consistency
    // Get the phonebox audio ref from CanvasContext
    const phoneboxAudio = getPhoneboxAudio ? getPhoneboxAudio() : null;
    let completionAudio;

    if (!phoneboxAudio) {
      logger.error('Phonebox audio ref not available - cannot play hangup audio');
      // Fallback: create new audio if phonebox audio is not available
      completionAudio = new Audio('/audio/hangup.mp3');
      hangupAudioRef.current = completionAudio;
      completionAudio.muted = false;
      completionAudio.volume = isSoundOn ? PHONE_AUDIO_VOLUME : 0;
      logger.debug('Created new hangup audio (fallback) with src:', completionAudio.src);
    } else {
      // Reuse the phonebox audio object - stop any current playback and change src
      // IMPORTANT: The phonebox audio's event listeners (play/ended) are still attached.
      // CombinedScene's handleAudioStart checks the src to detect hangup audio and skips
      // setting up timeouts and dispatching events. handleAudioEnd also checks the flag
      // to prevent dispatching phoneAudioEnded event when hangup audio ends.
      completionAudio = phoneboxAudio;

      // Stop any current playback
      try {
        completionAudio.pause();
        completionAudio.currentTime = 0;
      } catch (error) {
        logger.warn('Error stopping phonebox audio before switching to hangup:', error);
      }

      // IMPORTANT: Set isCompleting flag BEFORE changing src to prevent question audio error handler from firing
      // This must happen before any audio operations to prevent race conditions
      // (isCompleting is already set above, but ensure the ref is also set)
      isCompletingRef.current = true;

      // Store hangup audio in ref IMMEDIATELY to prevent question audio error handler from processing it
      hangupAudioRef.current = completionAudio;

      // Change the audio source to hangup.mp3
      // CombinedScene's handleAudioStart will detect this is hangup audio by checking src.includes('hangup.mp3')
      // and will skip setting up timeouts and dispatching phoneAudioEnded events

      // IMPORTANT: Set src using a full URL to ensure it's set correctly
      // Using a relative path should work, but let's be explicit
      const hangupAudioSrc = '/audio/hangup.mp3';
      completionAudio.src = hangupAudioSrc;
      completionAudio.muted = false; // Keep unmuted so analyser can analyze audio
      completionAudio.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
      // Volume is controlled by gainNode (if connected), not audio element volume

      // Verify the src was set correctly
      if (!completionAudio.src.includes('hangup.mp3')) {
        logger.error('Failed to set hangup audio src correctly!', {
          expected: hangupAudioSrc,
          actual: completionAudio.src,
          currentSrc: completionAudio.currentSrc
        });
      }

      // IMPORTANT: Call load() after changing src to actually load the new audio file
      // This is required when reusing an Audio element with a new source
      try {
        completionAudio.load();
        logger.debug('Called load() on hangup audio', {
          src: completionAudio.src,
          currentSrc: completionAudio.currentSrc,
          readyState: completionAudio.readyState
        });
      } catch (error) {
        logger.warn('Error calling load() on hangup audio:', error);
      }

      logger.debug('Reusing phonebox audio object for hangup.mp3', {
        src: completionAudio.src,
        currentSrc: completionAudio.currentSrc,
        readyState: completionAudio.readyState
      });
    }

    // Flag to prevent duplicate hangup processing
    let hangupProcessed = false;

    const processHangup = () => {
      if (hangupProcessed) {
        logger.debug('Hangup already processed, skipping duplicate');
        return;
      }
      hangupProcessed = true;

      logger.debug('Processing hangup sequence');

      // Clear hangup timeout
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }

      // Clear subtitle text and timeouts
      setCurrentSubtitleText(null);
      setCurrentSubtitleTimeouts(null);

      if (onComplete) {
        onComplete(responses);
      }

      // Release the question sequence lock before hanging up
      if (questionSequenceLockRef.current) {
        logger.debug('QuestionSequence: Releasing lock on hangup:', questionSequenceLockRef.current);
        releaseQuestionSequenceLock(questionSequenceLockRef.current);
        questionSequenceLockRef.current = null;
      }

      // CRITICAL: Ensure speech recognition is stopped and stays stopped
      // Stop it again here to prevent it from restarting during hangup
      // ANDROID: Stop here since phone is hanging up
      forceStopSpeechRecognition();
      logger.debug('Force stopped speech recognition before hangup', { isAndroidChrome });

      // IMPORTANT: Ensure soundwaves are hidden
      setPhonePlaying(false);
      logger.debug('Stopped soundwaves visualization in processHangup');

      // IMPORTANT: Don't stop hangup audio here - let it play out completely
      // The hangup useEffect (line 2894-2905) checks isCompletingRef.current and won't stop hangup audio if completing
      // Hangup audio will play until it ends naturally or the 13 second timeout fires

      // IMPORTANT: Hang up the phone (puts phone back on hook) but DON'T reset isCompletingRef yet
      // The hangup useEffect will see isCompletingRef.current is true and skip ALL cleanup
      // This allows hangup audio to continue playing
      // isCompletingRef will be reset in onended handler or timeout handler after audio finishes
      hangup({ reason: 'complete' });

      // IMPORTANT: Reset the question sequence now that hangup audio has finished
      // This ensures everything is ready for the next time the phone is taken off hook
      // Note: This is safe to call here because isCompletingRef.current is already false
      // (it was reset in onended handler before processHangup was called)
      resetQuestionSequence();
      logger.debug('Reset question sequence after hangup audio finished');

      // DON'T reset isCompletingRef here - it's already been reset in onended handler
    };

    // Track if audio has started playing
    let audioStartedPlaying = false;
    let hangupTimeoutStarted = false;

    // IMPORTANT: The phone will NOT hang up until one of these conditions:
    // 1. The 12 second timeout expires AFTER audio starts playing (fallback)
    // 2. The audio ends naturally (onended handler)
    // The phone will NOT hang up immediately on errors - it waits for the timeout
    // The timeout will be started when audio actually starts playing (in onplay handler)

    completionAudio.onended = () => {
      logger.debug('Hangup audio ended naturally');
      // Clear the timeout since audio ended naturally
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }
      // Small delay to ensure audio has fully finished
      setTimeout(() => {
        // IMPORTANT: Get audio from context to ensure we're using the current object
        const currentAudio = getPhoneboxAudio ? getPhoneboxAudio() : hangupAudioRef.current;

        // Stop hangup audio now that it's finished
        if (currentAudio) {
          try {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            logger.debug('Stopped hangup audio after it finished playing');
          } catch (error) {
            console.warn('Error stopping hangup audio:', error);
          }
        }

        // Clear the ref
        hangupAudioRef.current = null;

        // IMPORTANT: Hide soundwaves when hangup audio ends
        setPhonePlaying(false);
        logger.debug('Stopped soundwaves visualization after hangup audio ended');

        // Reset isCompleting flag now that audio has finished
        setIsCompleting(false);
        isCompletingRef.current = false;

        // Now process hangup - this will reset the sequence
        processHangup();
      }, 100);
    };

    // Set up error handler AFTER processHangup is defined
    completionAudio.onerror = (error) => {
      console.error('Error playing completion audio:', error);
      logger.debug('Hangup audio error details:', {
        error: error.message || 'Unknown error',
        audioReadyState: completionAudio.readyState,
        audioSrc: completionAudio.src,
        errorCode: error?.target?.error?.code
      });
      // Clear any error state (don't show question audio error for hangup audio)
      setError(null);
      // Don't hang up immediately on error - wait for the 12 second timeout
      // The timeout will handle hanging up if audio fails
      // Only log the error, don't trigger immediate hangup
      logger.warn('Hangup audio error occurred, will wait for 12 second timeout before hanging up');
      // Start timeout if not already started (in case audio fails before playing)
      if (!hangupTimeoutStarted) {
        hangupTimeoutStarted = true;
        logger.debug('Starting hangup timeout after audio error', {
          timeoutMs: HANGUP_TIMEOUT_MS
        });
        hangupTimeoutRef.current = setTimeout(() => {
          hangupTimeoutRef.current = null;
          logger.debug('Starting hangup process after 12 second timeout (audio error case)');
          processHangup();
        }, HANGUP_TIMEOUT_MS);
      }
    };

    completionAudio.onplay = () => {
      audioStartedPlaying = true;

      // IMPORTANT: Get audio from context to ensure we're using the current object
      const currentAudio = getPhoneboxAudio ? getPhoneboxAudio() : completionAudio;

      logger.debug('Hangup audio started playing - starting 12 second timeout', {
        duration: currentAudio.duration,
        currentTime: currentAudio.currentTime
      });

      // Clear any existing timeout
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }

      hangupTimeoutStarted = true;
      hangupTimeoutRef.current = setTimeout(() => {
        hangupTimeoutRef.current = null;

        // IMPORTANT: Get audio from context again to ensure we're using the current object
        const currentAudioInTimeout = getPhoneboxAudio ? getPhoneboxAudio() : hangupAudioRef.current;

        logger.debug('13 second timeout fired - triggering hangup but letting audio continue', {
          audioStartedPlaying,
          audioPaused: currentAudioInTimeout?.paused,
          audioEnded: currentAudioInTimeout?.ended,
          audioReadyState: currentAudioInTimeout?.readyState,
          currentTime: currentAudioInTimeout?.currentTime,
          duration: currentAudioInTimeout?.duration
        });

        // IMPORTANT: Hide soundwaves after timeout - audio will continue playing but soundwaves should stop
        setPhonePlaying(false);
        logger.debug('Stopped soundwaves visualization after 13 second timeout');

        // IMPORTANT: Don't stop hangup audio - let it play until it ends naturally
        // Just trigger the hangup process (puts phone back on hook) but keep audio playing
        // Keep isCompletingRef.current true so hangup useEffect doesn't stop the audio
        // The onended handler will stop the audio and do final cleanup when audio finishes

        // Trigger hangup (puts phone back on hook) - audio will continue playing
        // The hangup useEffect will skip ALL cleanup because isCompletingRef.current is still true
        // This allows audio to keep playing until onended fires
        hangup({ reason: 'complete' });

        // DON'T reset isCompletingRef here - keep it true so hangup useEffect doesn't stop audio
        // The onended handler will reset it and do cleanup when audio finishes
      }, HANGUP_TIMEOUT_MS);
    };

    completionAudio.onplaying = () => {
      audioStartedPlaying = true;

      // IMPORTANT: Get audio from context to ensure we're using the current object
      const currentAudio = getPhoneboxAudio ? getPhoneboxAudio() : completionAudio;
      logger.debug('Hangup audio is playing', {
        duration: currentAudio?.duration,
        currentTime: currentAudio?.currentTime
      });

      // Also start timeout here if not already started (backup in case onplay didn't fire)
      if (!hangupTimeoutStarted && !hangupTimeoutRef.current) {
        hangupTimeoutStarted = true;
        logger.debug('Hangup audio playing - starting 12 second timeout (backup)');
        hangupTimeoutRef.current = setTimeout(() => {
          hangupTimeoutRef.current = null;

          // IMPORTANT: Get audio from context again to ensure we're using the current object
          const currentAudioInTimeout = getPhoneboxAudio ? getPhoneboxAudio() : hangupAudioRef.current;

          logger.debug('Starting hangup process after 12 second timeout (from onplaying backup)', {
            audioPaused: currentAudioInTimeout?.paused,
            audioEnded: currentAudioInTimeout?.ended
          });

          // Trigger hangup but keep audio playing
          hangup({ reason: 'complete' });
        }, HANGUP_TIMEOUT_MS);
      }
    };

    // Also listen for loadedmetadata to get duration early
    completionAudio.onloadedmetadata = () => {
      logger.debug('Hangup audio metadata loaded', {
        duration: completionAudio.duration,
        readyState: completionAudio.readyState
      });
    };

    // Play the completion message - ensure audio context is resumed first
    const playHangupAudio = async () => {
      try {
        // IMPORTANT: Get audio from context to ensure we're using the current object
        // This ensures the audio object isn't stale if context changes
        const currentAudio = getPhoneboxAudio ? getPhoneboxAudio() : completionAudio;
        if (!currentAudio) {
          logger.error('Phonebox audio not available from context - cannot play hangup audio');
          processHangup();
          return;
        }

        // Ensure phone is still off-hook before playing hangup audio
        if (!phoneboxState.isOffHook) {
          logger.debug('Phone already hung up, skipping hangup audio');
          processHangup();
          return;
        }

        // Ensure audio context is resumed before playing hangup audio
        if (phoneboxState.audioContext && phoneboxState.audioContext.state === 'suspended') {
          await phoneboxState.audioContext.resume();
          logger.audio('Audio context resumed before playing hangup audio');
        }

        // Wait for audio to be ready to play
        const checkAudioReady = () => {
          return new Promise((resolve, reject) => {
            // IMPORTANT: Use currentAudio from context, not completionAudio local variable
            // This ensures we're always using the audio object from context
            const audioToCheck = getPhoneboxAudio ? getPhoneboxAudio() : currentAudio;
            if (!audioToCheck) {
              reject(new Error('Audio object not available from context'));
              return;
            }

            // Check current readyState
            logger.debug('Checking hangup audio readyState:', audioToCheck.readyState, 'src:', audioToCheck.src);

            if (audioToCheck.readyState >= 2) { // HAVE_CURRENT_DATA or higher
              logger.debug('Hangup audio already ready, readyState:', audioToCheck.readyState);
              resolve();
              return;
            }

            // Ensure audio is loading
            if (audioToCheck.readyState === 0) {
              logger.debug('Hangup audio not loaded yet, calling load()');
              try {
                audioToCheck.load();
              } catch (error) {
                logger.warn('Error calling load() in checkAudioReady:', error);
              }
            }

            const onCanPlay = () => {
              audioToCheck.removeEventListener('canplay', onCanPlay);
              audioToCheck.removeEventListener('canplaythrough', onCanPlayThrough);
              audioToCheck.removeEventListener('error', onError);
              logger.debug('Hangup audio can play, readyState:', audioToCheck.readyState);
              resolve();
            };

            const onCanPlayThrough = () => {
              audioToCheck.removeEventListener('canplay', onCanPlay);
              audioToCheck.removeEventListener('canplaythrough', onCanPlayThrough);
              audioToCheck.removeEventListener('error', onError);
              logger.debug('Hangup audio can play through, readyState:', audioToCheck.readyState);
              resolve();
            };

            const onError = (error) => {
              audioToCheck.removeEventListener('canplay', onCanPlay);
              audioToCheck.removeEventListener('canplaythrough', onCanPlayThrough);
              audioToCheck.removeEventListener('error', onError);
              logger.error('Hangup audio error during loading:', {
                error: error,
                src: audioToCheck.src,
                readyState: audioToCheck.readyState,
                errorCode: error?.target?.error?.code
              });
              reject(error);
            };

            audioToCheck.addEventListener('canplay', onCanPlay);
            audioToCheck.addEventListener('canplaythrough', onCanPlayThrough);
            audioToCheck.addEventListener('error', onError);

            // Timeout after 5 seconds (increased from 3)
            setTimeout(() => {
              audioToCheck.removeEventListener('canplay', onCanPlay);
              audioToCheck.removeEventListener('canplaythrough', onCanPlayThrough);
              audioToCheck.removeEventListener('error', onError);
              if (audioToCheck.readyState >= 2) {
                logger.debug('Hangup audio ready after timeout, readyState:', audioToCheck.readyState);
                resolve();
              } else {
                logger.error('Hangup audio loading timeout:', {
                  readyState: audioToCheck.readyState,
                  src: audioToCheck.src,
                  currentSrc: audioToCheck.currentSrc
                });
                reject(new Error('Audio loading timeout'));
              }
            }, 5000);
          });
        };

        await checkAudioReady();

        // Check again if phone is still off-hook after loading
        if (!phoneboxState.isOffHook) {
          logger.debug('Phone hung up during audio load, skipping hangup audio');
          processHangup();
          return;
        }

        // Play the audio
        logger.debug('Attempting to play hangup audio', {
          readyState: completionAudio.readyState,
          src: completionAudio.src,
          currentSrc: completionAudio.currentSrc,
          paused: completionAudio.paused,
          muted: completionAudio.muted,
          volume: completionAudio.volume,
          isSoundOn: isSoundOn
        });

        try {
          const playPromise = completionAudio.play();

          if (playPromise !== undefined) {
            await playPromise;
            logger.debug('Hangup audio started playing successfully');
          } else {
            logger.debug('Hangup audio play() returned undefined, assuming it started');
          }

          // Verify audio is actually playing
          // Give it a moment to start
          await new Promise(resolve => setTimeout(resolve, 100));

          if (completionAudio.paused) {
            logger.warn('Hangup audio is paused after play() call - this might indicate an issue');
            logger.debug('Audio state:', {
              paused: completionAudio.paused,
              ended: completionAudio.ended,
              readyState: completionAudio.readyState,
              currentTime: completionAudio.currentTime,
              duration: completionAudio.duration
            });
          } else {
            logger.debug('Hangup audio is playing successfully');
          }
        } catch (playError) {
          logger.error('Error calling play() on hangup audio:', playError);
          throw playError;
        }
      } catch (error) {
        console.error('Could not play completion audio:', error);
        logger.debug('Hangup audio playback error details:', {
          error: error.message,
          name: error.name,
          audioState: completionAudio.readyState,
          audioSrc: completionAudio.src,
          paused: completionAudio.paused
        });
        // Clear any error state (don't show question audio error for hangup audio)
        setError(null);
        // Don't hang up immediately on error - wait for the 15 second timeout
        // The timeout will handle hanging up if audio fails
        logger.warn('Hangup audio playback error occurred, will wait for 15 second timeout before hanging up');
        // The existing 15 second timeout will handle the hangup
      }
    };

    // Call playHangupAudio to start playing the hangup audio
    logger.debug('Calling playHangupAudio() to start hangup audio playback');
    playHangupAudio().catch((error) => {
      // This error is already logged in playHangupAudio, but log here too for visibility
      logger.error('playHangupAudio() failed:', error);
      // The 15 second timeout will still trigger processHangup() even if audio fails
    });
  }, [responses, onComplete, hangup, releaseQuestionSequenceLock, isCompleting, isSoundOn, trackQuestionEvent, markQuestionAudioInactive, phoneboxState, getPhoneboxAudio, forceStopSpeechRecognition, isAndroidChrome, resetQuestionSequence, setPhonePlaying]);


  // Upload audio response to API (background upload, doesn't block)
  const uploadAudioResponse = useCallback(async (response, background = false) => {
    // Always set uploading state to show indicator (even for background uploads)
    setIsUploading(true);
    setUploadError(null);

    // Track this upload promise
    const uploadPromise = (async () => {
      try {
        logger.debug('Uploading audio response:', response.questionId, background ? '(background)' : '');

        const formData = new FormData();
        formData.append('submission_id', submissionId);
        formData.append('question_id', response.questionId);
        formData.append('question_text', response.questionText);
        formData.append('question_audio_file', response.questionAudioFile);

        // Determine file extension based on MIME type
        let extension = 'webm';
        if (response.audioBlob.type.includes('mp4')) {
          extension = 'mp4';
        } else if (response.audioBlob.type.includes('wav')) {
          extension = 'wav';
        } else if (response.audioBlob.type.includes('ogg')) {
          extension = 'ogg';
        } else if (response.audioBlob.type.includes('webm')) {
          extension = 'webm'; // Handle both audio/webm and video/webm
        }

        formData.append('audio_file', response.audioBlob, `${response.questionId}_response.${extension}`);
        formData.append('duration', response.duration.toString());

        // Add transcribed text from speech recognition
        const confirmedTranscription = confirmedTranscriptions[response.questionId];
        if (confirmedTranscription && confirmedTranscription.trim().length > 0) {
          formData.append('transcribed_text', confirmedTranscription.trim());
          logger.debug('Including confirmed transcription:', confirmedTranscription.trim());
        } else {
          logger.debug('No confirmed transcription available for', response.questionId);
        }

        // Debug: Log the actual blob details
        logger.debug('Audio blob details:', {
          size: response.audioBlob.size,
          type: response.audioBlob.type,
          lastModified: response.audioBlob.lastModified
        });

        const uploadResponse = await makeAuthenticatedRequest('/api/audio-responses.php', {
          method: 'POST',
          body: formData
        });

        const result = await uploadResponse.json();

        if (result.success) {
          logger.debug('Audio response uploaded successfully:', result.data);
          // Check if there are any other pending uploads before clearing the upload state
          pendingUploadsRef.current.delete(response.questionId);
          if (pendingUploadsRef.current.size === 0) {
            setIsUploading(false);
          }
          trackQuestionEvent('question_response_uploaded', {
            question_id: response.questionId,
            duration_ms: response.duration,
            duplicate: false
          });
          return result.data;
        } else {
          console.error('Failed to upload audio response:', result.message, result.errors);

          // Handle CSRF token errors specifically
          if (result.errors && result.errors.csrf) {
            const errorMessage = 'Security token expired. Please try again.';
            setUploadError(errorMessage);
            pendingUploadsRef.current.delete(response.questionId);
            if (pendingUploadsRef.current.size === 0) {
              setIsUploading(false);
            }
            throw new Error(errorMessage);
          }

          // Handle duplicate submission errors
          if (result.errors && result.errors.duplicate) {
            logger.debug('Duplicate submission detected, continuing to next question');
            // Don't throw error for duplicates, just continue
            pendingUploadsRef.current.delete(response.questionId);
            if (pendingUploadsRef.current.size === 0) {
              setIsUploading(false);
            }
            trackQuestionEvent('question_response_uploaded', {
              question_id: response.questionId,
              duration_ms: response.duration,
              duplicate: true
            });
            return { success: true, duplicate: true };
          }

          const errorMessage = result.message || 'Upload failed';
          setUploadError(errorMessage);
          pendingUploadsRef.current.delete(response.questionId);
          if (pendingUploadsRef.current.size === 0) {
            setIsUploading(false);
          }
          trackQuestionEvent('question_response_upload_failed', {
            question_id: response.questionId,
            error_type: 'api_error'
          });
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Error uploading audio response:', error);
        let errorMessage = 'Network error during upload';
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          errorMessage = 'Cannot connect to server. Please check your connection.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        setUploadError(errorMessage);
        pendingUploadsRef.current.delete(response.questionId);
        if (pendingUploadsRef.current.size === 0) {
          setIsUploading(false);
        }
        trackQuestionEvent('question_response_upload_failed', {
          question_id: response.questionId,
          error_type: error?.name || 'unknown'
        });
        throw error;
      } finally {
        // Note: pendingUploadsRef cleanup is handled in catch/success blocks above
      }
    })();

    // Store the promise for tracking
    pendingUploadsRef.current.set(response.questionId, uploadPromise);

    return uploadPromise;
  }, [confirmedTranscriptions, submissionId, makeAuthenticatedRequest, trackQuestionEvent]);

  // Process the recorded response
  const processResponse = useCallback(async (response) => {
    logger.debug('Processing response:', response);

    // Prevent race conditions - only allow one response processing at a time
    if (isProcessingResponse) {
      logger.debug('Response processing already in progress, skipping duplicate call');
      return;
    }

    // Check if this question has already been submitted
    if (submittedQuestions.has(response.questionId)) {
      logger.debug('Question already submitted, skipping duplicate submission:', response.questionId);
      return;
    }

    // Additional safety check: verify we're on the correct question
    const expectedQuestionId = questions[currentQuestionIndex]?.id;
    if (response.questionId !== expectedQuestionId) {
      console.error('Question ID mismatch! Expected:', expectedQuestionId, 'Got:', response.questionId);
      return;
    }

    // Critical safety check: verify phone is still off-hook
    if (!phoneboxState.isOffHook) {
      logger.debug('Phone has been hung up - stopping response processing');
      setIsProcessingResponse(false);
      return;
    }

    // Set processing flag to prevent race conditions
    setIsProcessingResponse(true);

    // Check if this is the final question
    const isFinalQuestion = currentQuestionIndex === questions.length - 1;

    // If this is the final question, stop speech recognition and microphone before uploading
    // ANDROID: Stop speech recognition here since we're completing
    if (isFinalQuestion) {
      logger.debug('Final question detected - stopping speech recognition and microphone', { isAndroidChrome });

      // Stop speech recognition (Android: stops here since completing)
      forceStopSpeechRecognition();

      // Stop microphone stream
      // Microphone cleanup is handled by MicrophoneContext via events
      // if (cleanupMicrophone) {
      //   cleanupMicrophone();
      //   logger.debug('Microphone stopped for final question');
      // }
    }

    // Mark this question as submitted
    setSubmittedQuestions(prev => new Set(prev).add(response.questionId));
    logger.debug('Marking question as submitted:', response.questionId);

    setLocalQuestionState('processing');

    // Start upload in background (non-blocking)
    // For non-final questions, continue immediately to next question
    // For final question, we'll wait for upload before completing

    // Start upload in background - don't await it for non-final questions
    const uploadPromise = uploadAudioResponse(response, !isFinalQuestion);

    // Handle upload result in background (non-blocking)
    uploadPromise
      .then((uploadedResponse) => {
        logger.debug('Response uploaded with ID:', uploadedResponse?.id || 'unknown');
        // Clear any previous upload errors on success
        setUploadError(null);
      })
      .catch((error) => {
        console.error('Background upload failed (non-blocking):', error);
        // Upload error is already set in uploadAudioResponse
        // Don't block question progression for upload errors
      });

    // Move to next question or complete immediately (don't wait for upload)
    if (currentQuestionIndex < questions.length - 1) {
      // Non-final question: proceed immediately without waiting for upload
      clearTimeoutRef(nextQuestionTimeoutRef);
      nextQuestionTimeoutRef.current = setTimeout(() => {
        nextQuestionTimeoutRef.current = null;

        if (!phoneboxState.isOffHook) {
          logger.debug('Phone hung up during processing - stopping question advancement');
          setIsProcessingResponse(false);
          return;
        }

        setCurrentQuestionIndex(prev => prev + 1);
        setLocalQuestionState('idle');
        setIsProcessingResponse(false); // Reset processing flag

        clearTimeoutRef(autoPlayNextQuestionTimeoutRef);
        autoPlayNextQuestionTimeoutRef.current = setTimeout(() => {
          autoPlayNextQuestionTimeoutRef.current = null;
          if (phoneboxState.isOffHook && playQuestionRef.current) {
            playQuestionRef.current();
          } else {
            logger.debug('Phone hung up before next question - not starting');
          }
        }, 500);
      }, 1000);
    } else {
      // Final question: wait for upload to complete before finishing sequence
      // This ensures the final response is uploaded before hangup
      clearTimeoutRef(completionTimeoutRef);

      logger.debug('Final question detected - waiting for upload to complete before calling completeQuestionSequence', {
        uploadPromiseState: uploadPromise ? 'exists' : 'missing',
        isOffHook: phoneboxState.isOffHook,
        currentQuestionIndex,
        totalQuestions: questions.length
      });

      // Wait for upload to complete, but with a timeout fallback
      // Use Promise.resolve to handle already-resolved promises
      Promise.resolve(uploadPromise)
        .then((uploadedResponse) => {
          logger.debug('Final question upload promise resolved', {
            uploadedResponse: uploadedResponse?.id || 'no id',
            isOffHook: phoneboxState.isOffHook
          });

          // Clear any existing completion timeout
          clearTimeoutRef(completionTimeoutRef);

          // Call completeQuestionSequence immediately after upload completes
          // Small delay to ensure state is consistent
          completionTimeoutRef.current = setTimeout(() => {
            completionTimeoutRef.current = null;

            // Use ref to get current state (avoid stale closure)
            const currentIsOffHook = phoneboxStateRef.current?.isOffHook ?? phoneboxState.isOffHook;

            if (!currentIsOffHook) {
              logger.debug('Phone hung up during completion - stopping sequence');
              setIsProcessingResponse(false);
              return;
            }

            logger.debug('Calling completeQuestionSequence for final question', {
              isOffHook: currentIsOffHook,
              currentQuestionIndex,
              totalQuestions: questions.length
            });
            setIsProcessingResponse(false); // Reset processing flag
            completeQuestionSequence();
          }, 100);
        })
        .catch((error) => {
          // Even if upload fails, complete the sequence
          logger.debug('Final question upload failed, completing sequence anyway:', error);

          // Clear any existing completion timeout
          clearTimeoutRef(completionTimeoutRef);

          completionTimeoutRef.current = setTimeout(() => {
            completionTimeoutRef.current = null;

            // Use ref to get current state (avoid stale closure)
            const currentIsOffHook = phoneboxStateRef.current?.isOffHook ?? phoneboxState.isOffHook;

            if (!currentIsOffHook) {
              logger.debug('Phone hung up during completion - stopping sequence');
              setIsProcessingResponse(false);
              return;
            }

            logger.debug('Calling completeQuestionSequence after upload error', {
              isOffHook: currentIsOffHook,
              currentQuestionIndex,
              totalQuestions: questions.length
            });
            setIsProcessingResponse(false); // Reset processing flag
            completeQuestionSequence();
          }, 100);
        });

      // Also set a timeout fallback in case the promise never resolves
      const timeoutFallback = setTimeout(() => {
        logger.debug('Final question upload timeout fallback triggered - completing sequence');
        clearTimeoutRef(completionTimeoutRef);

        completionTimeoutRef.current = setTimeout(() => {
          completionTimeoutRef.current = null;

          const currentIsOffHook = phoneboxStateRef.current?.isOffHook ?? phoneboxState.isOffHook;

          if (!currentIsOffHook) {
            logger.debug('Phone hung up during completion timeout - stopping sequence');
            setIsProcessingResponse(false);
            return;
          }

          logger.debug('Calling completeQuestionSequence after timeout fallback');
          setIsProcessingResponse(false);
          completeQuestionSequence();
        }, 100);
      }, 10000); // 10 second timeout

      // Store timeout so we can clear it if promise resolves
      uploadPromise.finally(() => {
        clearTimeout(timeoutFallback);
      }).catch(() => {
        // Ignore errors - already handled above
      });
    }
  }, [currentQuestionIndex, questions, completeQuestionSequence, uploadAudioResponse, submittedQuestions, isProcessingResponse, phoneboxState.isOffHook, forceStopSpeechRecognition, isAndroidChrome]);

  // Update processResponse ref after it's defined
  useEffect(() => {
    processResponseRef.current = processResponse;
  }, [processResponse]);
  // Start recording
  const startRecording = useCallback(() => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'recording') {
      return;
    }

    if (!hasLiveStream) {
      logger.warn('Attempted to start recording without an active microphone stream - requesting microphone again');
      setError('Reconnecting microphone…');
      if (requestMicrophonePermission) {
        requestMicrophonePermission();
      }
      return;
    }

    audioChunksRef.current = [];
    recordingStartTimeRef.current = Date.now();
    console.log('[DEBUG] startRecording - Resetting flags for new recording:', {
      previousShouldReplay: shouldReplayQuestionRef.current,
      previousHasValidAnswer: hasValidAnswerRef.current,
      previousHasConfirmedAnswer: hasConfirmedAnswerRef.current,
      recorderState: mediaRecorderRef.current?.state,
      isRecording
    });
    shouldReplayQuestionRef.current = false; // Reset replay flag for new recording
    hasValidAnswerRef.current = false; // Reset valid answer flag for new recording
    hasConfirmedAnswerRef.current = false; // Reset confirmed answer flag for new recording

    // Reset all speech-related state for fresh recording
    // ANDROID: Don't reset speech recognition tracking if it's already active (runs continuously)
    setDetectedSpeech('');
    setCurrentSpeechConfidence(0);
    setSpeechDebugInfo('');
    setSpeechEndTimeoutActive(false);
    lastSpeechTimeRef.current = null;
    speechStartTimeRef.current = null;
    hasValidSpeechRef.current = false;

    if (isAndroidChrome && isSpeechRecognitionActiveRef.current) {
      // Android: Speech recognition is already running continuously - preserve state
      logger.debug('Android: Speech recognition already active - preserving state for continuous mode');
      // Don't reset speechRecognitionWasActiveRef - it should stay true
      // Don't reset isTranscribing - it should stay true
    } else {
      // Non-Android or speech recognition not active: Reset tracking for new recording
      speechRecognitionWasActiveRef.current = false;
      speechRecognitionStartedEarlyRef.current = false; // Reset early start flag for new recording
      // Note: isTranscribing will be set by onstart callback when speech recognition starts
    }

    // Clear confirmed transcription for current question if we were replaying
    // (indicated by shouldReplayQuestionRef being true before we reset it)
    const wasReplaying = shouldReplayQuestionRef.current;
    if (wasReplaying) {
      const currentQuestionId = questions[currentQuestionIndex]?.id;
      if (currentQuestionId) {
        setConfirmedTranscriptions(prev => {
          const updated = { ...prev };
          delete updated[currentQuestionId];
          return updated;
        });
        console.log('[DEBUG] startRecording - Cleared confirmed transcription for current question after replay:', currentQuestionId);
      }
    }

    console.log('[DEBUG] startRecording - Flags reset for new recording');
    mediaRecorderRef.current.start();
    setIsRecording(true);
    setLocalQuestionState('recording');

    // Start speech recognition for better speech end detection (only if not playing audio)
    // CRITICAL: Don't start speech recognition if we're completing the sequence (hangup audio playing)
    // Double-check that audio is not playing before starting speech recognition
    // ANDROID: Speech recognition runs continuously, so just check if it's active
    if (isAndroidChrome) {
      if (isSpeechRecognitionActiveRef.current) {
        logger.debug('Android: Speech recognition already running continuously - ensuring isTranscribing is set', {
          isTranscribing,
          isActive: isSpeechRecognitionActiveRef.current
        });
        // Speech recognition is already running continuously on Android
        // Ensure isTranscribing is set to true if speech recognition is active
        if (!isTranscribing) {
          setIsTranscribing(true);
          isTranscribingRef.current = true;
          logger.debug('Android: Set isTranscribing to true (speech recognition was already active)');
        }
      } else if (speechRecognitionSupported &&
        speechRecognitionRef.current &&
        !isCompletingRef.current &&
        !isPlayingAudio &&
        !isAudioPlayingRef.current &&
        !isQuestionAudioActiveRef.current &&
        (!questionAudioRef.current || questionAudioRef.current.paused)) {
        // Android: Speech recognition should be running but isn't - try to restart it
        logger.debug('Android: Speech recognition not active when recording starts - attempting to start', {
          hasRecognition: !!speechRecognitionRef.current,
          permissionState,
          hasLiveStream
        });
        try {
          speechRecognitionRef.current.start();
          speechRecognitionWasActiveRef.current = true;
          // isTranscribing will be set by onstart callback
          logger.debug('Android: Speech recognition start() called - waiting for onstart callback');
        } catch (error) {
          logger.warn('Android: Failed to start speech recognition when recording starts:', error);
        }
      } else {
        logger.debug('Android: Cannot start speech recognition - conditions not met', {
          speechRecognitionSupported,
          hasRecognition: !!speechRecognitionRef.current,
          isCompleting: isCompletingRef.current,
          isPlayingAudio,
          isAudioPlayingRef: isAudioPlayingRef.current,
          isQuestionAudioActive: isQuestionAudioActiveRef.current
        });
      }
    } else {
      // Non-Android: Use the original behavior
      // If we started speech recognition early (user gesture preservation), check if it's still active
      if (speechRecognitionStartedEarlyRef.current && isSpeechRecognitionActiveRef.current) {
        logger.debug('Speech recognition already active from early start (user gesture preserved)');
        // Speech recognition is already running from the early start - no need to start again
      } else if (speechRecognitionSupported &&
        !isCompletingRef.current && // CRITICAL: Don't start during completion
        !isPlayingAudio &&
        !isAudioPlayingRef.current &&
        !isQuestionAudioActiveRef.current &&
        (!questionAudioRef.current || questionAudioRef.current.paused)) {
        // Speech recognition wasn't started early or failed - try to start it now
        logger.debug('Starting speech recognition now (was not started early)');
        startSpeechRecognition();
      } else if (speechRecognitionSupported && (isCompletingRef.current || isPlayingAudio || isAudioPlayingRef.current || isQuestionAudioActiveRef.current)) {
        logger.debug('Not starting speech recognition - completing sequence or audio is playing/active', {
          isCompleting: isCompletingRef.current,
          isPlayingAudio,
          isAudioPlayingRef: isAudioPlayingRef.current,
          isQuestionAudioActive: isQuestionAudioActiveRef.current,
          startedEarly: speechRecognitionStartedEarlyRef.current,
          isActive: isSpeechRecognitionActiveRef.current
        });
      }

      // Reset the early start flag for next recording (non-Android only)
      speechRecognitionStartedEarlyRef.current = false;
    }

    // Set max duration timeout as fallback
    maxDurationTimeoutRef.current = setTimeout(() => {
      logger.debug('Max duration reached, stopping recording...');
      stopRecording();
    }, questions[currentQuestionIndex].maxDuration);

    // Set a shorter fallback timeout for speech recognition issues (5 seconds after speech detected)
    speechFallbackTimeoutRef.current = setTimeout(() => {
      if (isRecording && lastSpeechTimeRef.current && hasValidSpeechRef.current) {
        const timeSinceLastSpeech = Date.now() - lastSpeechTimeRef.current;
        if (timeSinceLastSpeech > 5000) { // 5 seconds since last speech
          logger.debug('Speech fallback timeout reached - stopping recording after 5 seconds of silence');
          stopRecording();
        }
      }
    }, 5000);

    // Clear any existing no-speech timeout when starting a new recording
    if (noSpeechTimeoutRef.current) {
      clearTimeout(noSpeechTimeoutRef.current);
      noSpeechTimeoutRef.current = null;
    }

    logger.debug(`Started recording for question: ${questions[currentQuestionIndex].text} (speech recognition + ${questions[currentQuestionIndex].maxDuration / 1000}s max duration)`);

    if (speechRecognitionSupported) {
      logger.debug('Speech recognition active');
    } else {
      logger.warn('Speech recognition not supported - recording will rely on max duration timeout only');
    }
  }, [currentQuestionIndex, questions, stopRecording, speechRecognitionSupported, startSpeechRecognition, isPlayingAudio, isRecording, hasLiveStream, requestMicrophonePermission, isAndroidChrome, isTranscribing, permissionState]);

  // Update startRecording ref after it's defined
  useEffect(() => {
    startRecordingRef.current = startRecording;
  }, [startRecording]);

  // Play question audio using single audio player
  const playQuestion = useCallback(() => {
    logger.debug('playQuestion called', {
      isHungUp: isHungUpRef.current,
      isOffHook: phoneboxState.isOffHook,
      isPlayingAudio,
      isAudioPlaying: isAudioPlayingRef.current,
      currentQuestionIndex,
      questionsLength: questions?.length
    });

    // Check if phone has been hung up - if so, don't play audio
    if (isHungUpRef.current || !phoneboxState.isOffHook) {
      logger.debug('playQuestion called but phone is hung up - aborting');
      return;
    }

    // Prevent multiple simultaneous play attempts
    if (isPlayingAudio || isAudioPlayingRef.current) {
      logger.debug('Audio already playing, skipping playQuestion');
      return;
    }

    // Ensure audio player is initialized
    if (!questionAudioRef.current) {
      initializeAudioPlayer();
    }

    // Validate question exists
    if (!questions || currentQuestionIndex >= questions.length || currentQuestionIndex < 0) {
      logger.error('Invalid question index:', currentQuestionIndex, 'questions length:', questions?.length);
      setError('Invalid question index');
      if (!isHungUpRef.current) {
        logger.debug('Invalid question index - automatically hanging up phone');
        hangup({ reason: 'error' });
      }
      return;
    }

    const currentQuestion = questions[currentQuestionIndex];

    // Validate question object exists
    if (!currentQuestion) {
      logger.error('Question is undefined at index:', currentQuestionIndex);
      setError('Question not found');
      if (!isHungUpRef.current) {
        logger.debug('Question undefined - automatically hanging up phone');
        hangup({ reason: 'error' });
      }
      return;
    }

    // Validate audio file path exists and is valid
    if (!currentQuestion.audioFile || typeof currentQuestion.audioFile !== 'string' || currentQuestion.audioFile.trim() === '') {
      logger.error('Invalid audio file path for question:', {
        questionId: currentQuestion.id,
        questionIndex: currentQuestionIndex,
        audioFile: currentQuestion.audioFile,
        question: currentQuestion
      });
      setError('Invalid audio file path');
      if (!isHungUpRef.current) {
        logger.debug('Invalid audio file - automatically hanging up phone');
        hangup({ reason: 'error' });
      }
      return;
    }

    logger.debug(`Playing question ${currentQuestionIndex + 1}:`, {
      questionId: currentQuestion.id,
      audioFile: currentQuestion.audioFile
    });
    trackQuestionEvent('question_prompt_started', {
      question_id: currentQuestion.id
    });

    // Clear any existing error before starting
    setError(null);

    // Force stop speech recognition before playing audio
    // ANDROID: Don't stop speech recognition - it runs continuously
    // Re-evaluate isAndroidChrome to avoid closure issues
    const currentIsAndroidChrome = typeof window !== 'undefined' &&
      /android/i.test(navigator.userAgent) &&
      /chrome/i.test(navigator.userAgent) &&
      !/firefox/i.test(navigator.userAgent);
    if (!currentIsAndroidChrome) {
      forceStopSpeechRecognition();
    } else {
      logger.debug('Android: Keeping speech recognition running during audio playback');
    }

    // Stop any currently playing audio
    if (questionAudioRef.current && !questionAudioRef.current.paused) {
      logger.debug('Stopping currently playing audio');
      questionAudioRef.current.pause();
      questionAudioRef.current.currentTime = 0;
    }

    // Store current question text and timeouts for subtitle
    currentQuestionTextRef.current = currentQuestion.text;
    currentQuestionTimeoutsRef.current = currentQuestion.subtitleTimeouts || null;

    // Set loading state BEFORE setting src to prevent race conditions
    setIsLoadingAudio(true);
    isLoadingAudioRef.current = true;
    setLocalQuestionState('loading');
    // Clear any pending errors and timeouts when starting to load new audio
    clearTimeoutRef(pendingErrorTimeoutRef);
    pendingErrorRef.current = null;
    setError(null);

    // Clear any existing src first to prevent stale URLs
    const previousSrc = questionAudioRef.current.src;
    if (previousSrc && previousSrc !== currentQuestion.audioFile) {
      logger.debug('Clearing previous audio source:', previousSrc);
      questionAudioRef.current.src = '';
    }

    // Set the new audio source with validation
    const audioFileUrl = currentQuestion.audioFile;
    logger.audio('Setting audio source:', audioFileUrl);

    // Double-check the URL is valid before setting
    if (!audioFileUrl || audioFileUrl.trim() === '' || audioFileUrl === window.location.href) {
      logger.error('Attempted to set invalid audio source:', {
        audioFileUrl,
        currentUrl: window.location.href,
        questionId: currentQuestion.id
      });
      setIsLoadingAudio(false);
      isLoadingAudioRef.current = false;
      setError('Invalid audio file path');
      if (!isHungUpRef.current) {
        logger.debug('Invalid audio source detected - automatically hanging up phone');
        hangup({ reason: 'error' });
      }
      return;
    }

    questionAudioRef.current.src = audioFileUrl;
    questionAudioRef.current.load(); // Force reload of the new source
    questionAudioRef.current.muted = false; // Keep unmuted so analyser can analyze audio
    questionAudioRef.current.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
    // Volume is controlled by gainNode, not audio element volume

    if (questionAudioCooldownTimeoutRef.current) {
      clearTimeout(questionAudioCooldownTimeoutRef.current);
      questionAudioCooldownTimeoutRef.current = null;
    }
    isQuestionAudioActiveRef.current = true;

    // Play the audio
    questionAudioRef.current.play().catch(error => {
      // AbortError is expected when audio is stopped/changed before playing starts
      if (error.name === 'AbortError') {
        logger.debug('Audio play() aborted (this is expected when changing questions)');
        setIsLoadingAudio(false);
        isLoadingAudioRef.current = false;
        markQuestionAudioInactive(true);
        // Clear any pending errors for abort
        pendingErrorRef.current = null;
        return;
      }

      // NotAllowedError can occur if autoplay is blocked, but we handle this elsewhere
      if (error.name === 'NotAllowedError') {
        logger.warn('Audio play() not allowed (autoplay may be blocked):', error);
        setIsLoadingAudio(false);
        isLoadingAudioRef.current = false;
        markQuestionAudioInactive(true);
        // Clear any pending errors for not allowed
        pendingErrorRef.current = null;
        // Don't set error - this is usually handled by user interaction
        return;
      }

      // For other errors, log and set error state
      console.error('Could not play question audio:', error);
      setIsLoadingAudio(false);
      setIsPlayingAudio(false);
      isAudioPlayingRef.current = false;
      markQuestionAudioInactive(true);
      setPhonePlaying(false);

      // Only set error if there's actually an error message and it's not a known non-fatal error
      if (error && error.message && !error.message.includes('abort')) {
        setError(`Could not play question audio: ${error.message}`);
        // Automatically hang up on error
        if (!isHungUpRef.current) {
          logger.debug('Error occurred - automatically hanging up phone');
          hangup({ reason: 'error' });
        }
      } else if (error && error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
        setError('Could not play question audio');
        // Automatically hang up on error
        if (!isHungUpRef.current) {
          logger.debug('Error occurred - automatically hanging up phone');
          hangup({ reason: 'error' });
        }
      }
    });
  }, [currentQuestionIndex, questions, isPlayingAudio, setPhonePlaying, initializeAudioPlayer, forceStopSpeechRecognition, trackQuestionEvent, markQuestionAudioInactive, hangup, phoneboxState.isOffHook]);

  // Update playQuestion ref after it's defined
  useEffect(() => {
    playQuestionRef.current = playQuestion;
  }, [playQuestion]);

  // Generate unique submission ID on mount (only if not already set)
  useEffect(() => {
    const generateSubmissionId = () => {
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      return `submission_${timestamp}_${randomId}`;
    };

    // Check if we have a submission ID in sessionStorage (persists across remounts)
    const storedSubmissionId = sessionStorage.getItem('questionSequenceSubmissionId');

    if (storedSubmissionId) {
      // Use the existing submission ID from sessionStorage
      submissionIdRef.current = storedSubmissionId;
      setSubmissionId(storedSubmissionId);
      logger.debug('Resuming existing session with submission ID:', storedSubmissionId);
    } else {
      // Generate a new submission ID
      const newSubmissionId = generateSubmissionId();
      submissionIdRef.current = newSubmissionId;
      setSubmissionId(newSubmissionId);
      sessionStorage.setItem('questionSequenceSubmissionId', newSubmissionId);
      logger.debug('Starting new session with submission ID:', newSubmissionId);
    }
  }, []);

  // Initialize audio player and speech recognition on mount
  useEffect(() => {
    initializeAudioPlayer();
    initializeSpeechRecognition();
  }, [initializeAudioPlayer, initializeSpeechRecognition]);

  // ANDROID: When microphone permission and stream become ready, start speech recognition if phone is off-hook
  // This handles the case where the event fired before permission/stream were ready
  useEffect(() => {
    if (!isAndroidChrome) return;

    // Check if all conditions are met to start speech recognition
    if (phoneboxState.isOffHook &&
      speechRecognitionSupported &&
      speechRecognitionRef.current &&
      permissionState === 'granted' &&
      hasLiveStream &&
      !isSpeechRecognitionActiveRef.current &&
      !isCompleting) {
      logger.debug('Android: All conditions met - starting speech recognition (permission/stream ready)', {
        isOffHook: phoneboxState.isOffHook,
        speechRecognitionSupported,
        hasRecognition: !!speechRecognitionRef.current,
        permissionState,
        hasLiveStream,
        isActive: isSpeechRecognitionActiveRef.current,
        isCompleting
      });

      // Small delay to ensure everything is ready and to allow event handler to run first
      const timeoutId = setTimeout(() => {
        // Double-check conditions (they might have changed)
        if (speechRecognitionRef.current &&
          phoneboxState.isOffHook &&
          permissionState === 'granted' &&
          hasLiveStream &&
          !isSpeechRecognitionActiveRef.current &&
          !isCompleting) {
          logger.debug('Android: Starting speech recognition now (permission/stream ready)');
          try {
            speechRecognitionRef.current.start();
            speechRecognitionWasActiveRef.current = true;
            // Note: setIsTranscribing(true) will be called by onstart callback
            logger.debug('Android: Speech recognition start() called - waiting for onstart callback');
          } catch (error) {
            logger.warn('Android: Failed to start speech recognition when ready:', error);
          }
        } else {
          logger.debug('Android: Conditions changed before timeout - not starting speech recognition', {
            hasRecognition: !!speechRecognitionRef.current,
            isOffHook: phoneboxState.isOffHook,
            permissionState,
            hasLiveStream,
            isActive: isSpeechRecognitionActiveRef.current,
            isCompleting
          });
        }
      }, 300); // Slightly longer delay to ensure event handler runs first

      return () => clearTimeout(timeoutId);
    }
  }, [isAndroidChrome, speechRecognitionSupported, phoneboxState.isOffHook, permissionState, hasLiveStream, isCompleting]);

  // React to global sound toggle - update gain node instead of audio element volume
  useEffect(() => {
    isSoundOnRef.current = isSoundOn; // Update ref with current sound state

    if (questionGainNodeRef.current) {
      questionGainNodeRef.current.gain.value = isSoundOn ? 1 : 0;
    }
    // Also update audio element volume as fallback (though it doesn't affect Web Audio API)
    if (questionAudioRef.current) {
      questionAudioRef.current.muted = false; // Keep unmuted so analyser can analyze audio
      questionAudioRef.current.volume = PHONE_AUDIO_VOLUME; // Keep at normal volume for Web Audio API
    }

    // If sound is turned on while audio is playing, clear the sound-off timeout
    if (isSoundOn && soundOffTimeoutRef.current) {
      clearTimeout(soundOffTimeoutRef.current);
      soundOffTimeoutRef.current = null;
      logger.audio('Sound turned on - cleared sound-off timeout, allowing full playback');
    }
  }, [isSoundOn]);

  // Listen for phone hangup (when isOffHook becomes false)
  useEffect(() => {
    if (!phoneboxState.isOffHook) {
      // CRITICAL: If we're completing the sequence (hangup audio playing), skip ALL cleanup
      // The hangup audio needs to play out completely, and processHangup() will handle cleanup after audio finishes
      if (isCompletingRef.current) {
        logger.debug('Phone hung up during completion - skipping cleanup to allow hangup audio to finish');
        // Only stop speech recognition - don't do anything else
        // ANDROID: Stop here since phone is hanging up
        forceStopSpeechRecognition();
        return;
      }

      // Set hangup flag immediately to prevent race conditions
      isHungUpRef.current = true;

      logger.debug('Phone hung up - ending question sequence immediately');
      const wasActive = firstQuestionStartedRef.current;
      if (wasActive) {
        trackQuestionEvent('question_sequence_aborted', {
          reason: 'phone_hangup'
        });
      }

      // Force stop speech recognition immediately (regardless of recording state)
      // ANDROID: Stop here since phone is hanging up (useEffect will also handle this, but ensure it's stopped)
      forceStopSpeechRecognition();

      // Stop any ongoing recording
      if (isRecording) {
        stopRecording();
      }

      // Stop any playing audio and disconnect gain node
      if (questionAudioRef.current) {
        try {
          // Pause and clear audio
          questionAudioRef.current.pause();
          questionAudioRef.current.currentTime = 0;
          questionAudioRef.current.src = '';

          // Clear speech check interval if it exists
          if (questionAudioRef.current.speechCheckInterval) {
            clearInterval(questionAudioRef.current.speechCheckInterval);
            questionAudioRef.current.speechCheckInterval = null;
          }

          // Disconnect gain node to prevent any audio from playing
          if (questionGainNodeRef.current) {
            try {
              questionGainNodeRef.current.disconnect();
              questionGainNodeRef.current = null;
              logger.debug('Disconnected gain node on hangup');
            } catch (error) {
              console.warn('Error disconnecting gain node on hangup:', error);
            }
          }
        } catch (error) {
          console.warn('Error stopping audio on hangup:', error);
        }
      }

      // Stop hangup audio if it exists (manual hangup, not completion)
      if (hangupAudioRef.current) {
        try {
          hangupAudioRef.current.pause();
          hangupAudioRef.current.currentTime = 0;
          hangupAudioRef.current = null;
          logger.debug('Stopped hangup audio due to manual hangup');
        } catch (error) {
          console.warn('Error stopping hangup audio on manual hangup:', error);
        }
      }

      // Clear hangup timeout
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }

      // Clear all timeouts to prevent delayed audio playback
      clearAllTimeouts();

      // Reset audio state flags
      isAudioPlayingRef.current = false;
      setIsPlayingAudio(false);
      markQuestionAudioInactive(true);

      // Reset the question sequence
      resetQuestionSequence();

      // Release the microphone immediately so the browser stops showing it as in use
      // Release the microphone immediately so the browser stops showing it as in use
      // Managed by MicrophoneContext events now
      // if (cleanupMicrophone) {
      //   cleanupMicrophone();
      // }

      // Release the question sequence lock
      if (questionSequenceLockRef.current) {
        logger.debug('QuestionSequence: Releasing lock on phone hangup:', questionSequenceLockRef.current);
        releaseQuestionSequenceLock(questionSequenceLockRef.current);
        questionSequenceLockRef.current = null;
      }

      // Hang up the phone
      hangup({ reason: 'hangup' });
    } else {
      // Phone is off-hook again - reset hangup flag
      isHungUpRef.current = false;
    }
  }, [phoneboxState.isOffHook, isRecording, stopRecording, resetQuestionSequence, releaseQuestionSequenceLock, hangup, trackQuestionEvent, markQuestionAudioInactive, forceStopSpeechRecognition, clearAllTimeouts]);

  // Create hangup reset function and store in ref for use in speech recognition handlers
  const handleHangupReset = useCallback(() => {
    logger.debug('Force hangup reset triggered from speech recognition handler');

    // Set hangup flag immediately
    isHungUpRef.current = true;

    // Force stop speech recognition immediately
    // ANDROID: Stop here since resetting (useEffect will also handle this when phone goes on-hook)
    forceStopSpeechRecognition();

    // Stop any ongoing recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      stopRecording();
    }

    // Stop any playing audio and disconnect gain node
    if (questionAudioRef.current) {
      try {
        questionAudioRef.current.pause();
        questionAudioRef.current.currentTime = 0;
        questionAudioRef.current.src = '';

        // Clear speech check interval if it exists
        if (questionAudioRef.current.speechCheckInterval) {
          clearInterval(questionAudioRef.current.speechCheckInterval);
          questionAudioRef.current.speechCheckInterval = null;
        }

        // Disconnect gain node to prevent any audio from playing
        if (questionGainNodeRef.current) {
          try {
            questionGainNodeRef.current.disconnect();
            questionGainNodeRef.current = null;
            logger.debug('Disconnected gain node on force hangup');
          } catch (error) {
            console.warn('Error disconnecting gain node on force hangup:', error);
          }
        }
      } catch (error) {
        console.warn('Error stopping audio on force hangup:', error);
      }
    }

    // Don't stop hangup audio if we're completing the sequence - let it finish playing
    // Only stop it if we're hanging up for other reasons (manual hangup, error, etc.)
    if (!isCompletingRef.current && hangupAudioRef.current) {
      try {
        hangupAudioRef.current.pause();
        hangupAudioRef.current.currentTime = 0;
        hangupAudioRef.current = null;
        logger.debug('Stopped hangup audio due to force hangup');
      } catch (error) {
        console.warn('Error stopping hangup audio on force hangup:', error);
      }
    }

    // Clear hangup timeout if not completing (force hangup)
    if (!isCompletingRef.current && hangupTimeoutRef.current) {
      clearTimeout(hangupTimeoutRef.current);
      hangupTimeoutRef.current = null;
    }

    // Clear all timeouts
    clearAllTimeouts();

    // Reset audio state flags
    isAudioPlayingRef.current = false;
    setIsPlayingAudio(false);
    markQuestionAudioInactive(true);

    // Reset the question sequence
    resetQuestionSequence();

    // Release the microphone immediately
    // Managed by MicrophoneContext events now
    // if (cleanupMicrophone) {
    //   cleanupMicrophone();
    // }

    // Release the question sequence lock
    if (questionSequenceLockRef.current) {
      logger.debug('QuestionSequence: Releasing lock on force hangup:', questionSequenceLockRef.current);
      releaseQuestionSequenceLock(questionSequenceLockRef.current);
      questionSequenceLockRef.current = null;
    }

    // Hang up the phone
    hangup({ reason: 'hangup' });
  }, [forceStopSpeechRecognition, stopRecording, clearAllTimeouts, resetQuestionSequence, releaseQuestionSequenceLock, hangup, markQuestionAudioInactive]);

  // Update the ref whenever the function changes
  useEffect(() => {
    handleHangupResetRef.current = handleHangupReset;
  }, [handleHangupReset]);

  // Comprehensive cleanup when component unmounts
  useEffect(() => {
    return () => {
      logger.debug('QuestionSequence: Component unmounting - running comprehensive cleanup');

      // Force stop speech recognition immediately
      if (speechRecognitionRef.current && isSpeechRecognitionActiveRef.current) {
        try {
          speechRecognitionRef.current.stop();
          isSpeechRecognitionActiveRef.current = false;
          logger.debug('QuestionSequence: Stopped speech recognition on unmount');
        } catch (error) {
          console.error('Error stopping speech recognition on unmount:', error);
        }
      }

      // Stop media recorder if recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop();
          logger.debug('QuestionSequence: Stopped media recorder on unmount');
        } catch (error) {
          console.error('Error stopping media recorder on unmount:', error);
        }
      }

      // Clear all timeouts
      clearAllTimeouts();

      // Clear intervals (speech check interval stored on audio object)
      if (questionAudioRef.current && questionAudioRef.current.speechCheckInterval) {
        clearInterval(questionAudioRef.current.speechCheckInterval);
        questionAudioRef.current.speechCheckInterval = null;
        logger.debug('QuestionSequence: Cleared speech check interval on unmount');
      }

      // Stop and cleanup audio
      if (questionAudioRef.current) {
        try {
          questionAudioRef.current.pause();
          questionAudioRef.current.currentTime = 0;
          questionAudioRef.current.src = '';
          // Clear any interval stored on the audio object
          if (questionAudioRef.current.speechCheckInterval) {
            clearInterval(questionAudioRef.current.speechCheckInterval);
            questionAudioRef.current.speechCheckInterval = null;
          }
          logger.debug('QuestionSequence: Cleaned up audio on unmount');
        } catch (error) {
          console.warn('Error cleaning up audio on unmount:', error);
        }
      }

      // Release the microphone so the permission indicator turns off
      // Release the microphone so the permission indicator turns off
      // Managed by MicrophoneContext events now
      // if (cleanupMicrophone) {
      //   cleanupMicrophone();
      //   logger.debug('QuestionSequence: Released microphone on unmount');
      // }

      // Release the question sequence lock if still held
      if (questionSequenceLockRef.current) {
        logger.debug('QuestionSequence: Releasing lock on component unmount:', questionSequenceLockRef.current);
        releaseQuestionSequenceLock(questionSequenceLockRef.current);
        questionSequenceLockRef.current = null;
      }

      // Clear the submission ID from sessionStorage
      const currentSubmissionId = sessionStorage.getItem('questionSequenceSubmissionId');
      if (currentSubmissionId) {
        sessionStorage.removeItem('questionSequenceSubmissionId');
        logger.debug('QuestionSequence: Cleared submission ID from sessionStorage on unmount:', currentSubmissionId);
      }

      logger.debug('QuestionSequence: Cleanup complete');
    };
  }, [releaseQuestionSequenceLock, forceStopSpeechRecognition, clearAllTimeouts]);

  // Intro subtitles are now handled in App.jsx since QuestionSequence isn't mounted during intro playback

  // Listen for phoneAudioEnded custom event to start question sequence
  useEffect(() => {
    const handlePhoneAudioEnded = () => {
      logger.debug('phoneAudioEnded event received - checking if question sequence should start');

      // Only start question sequence if:
      // 1. Phone is still off-hook (user hasn't hung up)
      // 2. We're in question mode (properly controlled by CombinedScene)
      // 3. We haven't already started the first question
      if (phoneboxState.isOffHook &&
        phoneboxState.isInQuestionMode &&
        !firstQuestionStartedRef.current) {
        logger.debug('phoneAudioEnded: Conditions met - starting first question');
        // Start the first question after phone audio ends
        clearTimeoutRef(phoneAudioStartTimeoutRef);
        phoneAudioStartTimeoutRef.current = setTimeout(() => {
          phoneAudioStartTimeoutRef.current = null;
          // Double-check that phone is still off-hook before starting
          if (phoneboxState.isOffHook && playQuestionRef.current) {
            playQuestionRef.current();
          } else {
            logger.debug('phoneAudioEnded: Phone hung up during delay - not starting question');
          }
        }, 500);
      } else {
        logger.debug('phoneAudioEnded: Conditions not met - skipping question start', {
          isOffHook: phoneboxState.isOffHook,
          isInQuestionMode: phoneboxState.isInQuestionMode,
          firstQuestionStarted: firstQuestionStartedRef.current
        });
      }
    };

    // Add event listener for phoneAudioEnded custom event
    window.addEventListener('phoneAudioEnded', handlePhoneAudioEnded);

    return () => {
      // Clean up event listener
      window.removeEventListener('phoneAudioEnded', handlePhoneAudioEnded);
    };
  }, [phoneboxState.isOffHook, phoneboxState.isInQuestionMode]);

  // ANDROID-ONLY: Start speech recognition immediately when phone goes off-hook
  // Keep it running continuously until phone is hung up
  // This preserves user gesture context for Android Chrome
  // We listen to a custom event dispatched synchronously from the click handler
  useEffect(() => {
    if (!isAndroidChrome) return;

    const handleAndroidStartSpeechRecognition = (event) => {
      logger.debug('Android: Received speech recognition start event from click handler', event.detail);

      // Store that we received the event (even if not ready yet)
      // This helps us know that speech recognition should start when ready
      const shouldStartWhenReady = phoneboxState.isOffHook && !isCompleting;

      // Check if we're ready to start speech recognition NOW
      // ANDROID: Also verify stream has active audio tracks before starting
      const streamHasActiveTracks = stream && stream.getAudioTracks && stream.getAudioTracks().some(track => track.readyState === 'live');

      if (speechRecognitionSupported &&
        speechRecognitionRef.current &&
        permissionState === 'granted' &&
        hasLiveStream &&
        (!isAndroidChrome || streamHasActiveTracks) && // On Android, require active tracks
        !isSpeechRecognitionActiveRef.current &&
        shouldStartWhenReady) {
        // Android: Start speech recognition immediately (preserves user gesture from click handler)
        logger.debug('Android: Starting speech recognition immediately from click handler (user gesture preserved)', {
          hasStream: !!stream,
          hasActiveTracks: streamHasActiveTracks,
          trackCount: stream?.getAudioTracks?.()?.length || 0
        });
        try {
          speechRecognitionRef.current.start();
          speechRecognitionWasActiveRef.current = true;
          // Note: setIsTranscribing(true) will be called by onstart callback
          logger.debug('Android: Speech recognition start() called from event handler - waiting for onstart callback');
        } catch (error) {
          logger.warn('Android: Failed to start speech recognition from click handler:', error);
          // The useEffect watching permissionState/hasLiveStream will retry when ready
        }
      } else {
        logger.debug('Android: Not ready to start speech recognition yet (will start when ready)', {
          speechRecognitionSupported,
          hasRecognition: !!speechRecognitionRef.current,
          permissionState,
          hasLiveStream,
          hasStream: !!stream,
          hasActiveTracks: stream && stream.getAudioTracks ? stream.getAudioTracks().some(track => track.readyState === 'live') : false,
          isActive: isSpeechRecognitionActiveRef.current,
          isCompleting,
          isOffHook: phoneboxState.isOffHook,
          shouldStartWhenReady
        });
        // Don't worry - the useEffect watching permissionState/hasLiveStream will start it when ready
      }
    };

    const handleAndroidStopSpeechRecognition = (event) => {
      logger.debug('Android: Received speech recognition stop event from click handler', event.detail);

      if (speechRecognitionRef.current && isSpeechRecognitionActiveRef.current) {
        // Android: Stop speech recognition when phone is hung up
        logger.debug('Android: Stopping speech recognition on phone hangup');
        try {
          speechRecognitionRef.current.stop();
          isSpeechRecognitionActiveRef.current = false;
          setIsTranscribing(false);
          isTranscribingRef.current = false;
          speechRecognitionStartedEarlyRef.current = false;
        } catch (error) {
          logger.warn('Android: Error stopping speech recognition on hangup:', error);
        }
      }
    };

    // Listen to custom events dispatched synchronously from click handler
    window.addEventListener('androidStartSpeechRecognition', handleAndroidStartSpeechRecognition);
    window.addEventListener('androidStopSpeechRecognition', handleAndroidStopSpeechRecognition);

    return () => {
      window.removeEventListener('androidStartSpeechRecognition', handleAndroidStartSpeechRecognition);
      window.removeEventListener('androidStopSpeechRecognition', handleAndroidStopSpeechRecognition);
    };
  }, [isAndroidChrome, speechRecognitionSupported, permissionState, hasLiveStream, isCompleting, phoneboxState.isOffHook, stream]);

  // Fallback: Also try to start when conditions become ready (if event was missed)
  useEffect(() => {
    if (isAndroidChrome &&
      phoneboxState.isOffHook &&
      speechRecognitionSupported &&
      speechRecognitionRef.current &&
      permissionState === 'granted' &&
      hasLiveStream &&
      !isSpeechRecognitionActiveRef.current &&
      !isCompleting) {
      // Small delay to allow event handler to run first
      const timeoutId = setTimeout(() => {
        if (!isSpeechRecognitionActiveRef.current && phoneboxState.isOffHook) {
          logger.debug('Android: Fallback - starting speech recognition (event may have been missed)');
          try {
            speechRecognitionRef.current.start();
            speechRecognitionWasActiveRef.current = true;
          } catch (error) {
            logger.warn('Android: Fallback - failed to start speech recognition:', error);
          }
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isAndroidChrome, phoneboxState.isOffHook, speechRecognitionSupported, permissionState, hasLiveStream, isCompleting]);

  // Listen for isInQuestionMode state change to start question sequence
  useEffect(() => {
    if (phoneboxState.isInQuestionMode && !firstQuestionStartedRef.current) {
      // Store the lock ID for this sequence
      questionSequenceLockRef.current = phoneboxState.questionSequenceLockId;
      firstQuestionStartedRef.current = true;

      // Clear intro subtitle when questions start
      setCurrentSubtitleText(null);
      setCurrentSubtitleTimeouts(null);

      // Clear any stale errors when entering question mode
      setError(null);
      trackQuestionEvent('question_sequence_started', {
        total_questions: questions.length
      });

      logger.debug('QuestionSequence: Starting with lock ID:', questionSequenceLockRef.current);

      // Start the first question after a short delay
      clearTimeoutRef(questionModeStartTimeoutRef);
      questionModeStartTimeoutRef.current = setTimeout(() => {
        questionModeStartTimeoutRef.current = null;
        // Double-check that phone is still off-hook before starting
        if (phoneboxState.isOffHook && playQuestionRef.current) {
          playQuestionRef.current();
        } else {
          logger.debug('isInQuestionMode: Phone hung up during delay - not starting question');
        }
      }, 1000);
    }
  }, [phoneboxState.isInQuestionMode, phoneboxState.isOffHook, phoneboxState.questionSequenceLockId, questions.length, trackQuestionEvent]);

  // Initialize media recorder once when stream becomes available
  useEffect(() => {
    if (permissionState === 'granted' && stream && hasLiveStream && !mediaRecorderInitializedRef.current) {
      // Initialize media recorder directly in useEffect to avoid function recreation
      try {
        // Try different MIME types in order of preference
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/mp4';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
              mimeType = 'audio/wav';
              if (!MediaRecorder.isTypeSupported(mimeType)) {
                // Fallback to video/webm if audio formats aren't supported
                mimeType = 'video/webm';
              }
            }
          }
        }

        logger.debug('Using MediaRecorder MIME type:', mimeType);

        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: mimeType
        });

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
            logger.debug('Audio chunk received:', {
              size: event.data.size,
              type: event.data.type,
              totalChunks: audioChunksRef.current.length
            });
          }
        };

        mediaRecorderRef.current.onstop = () => {
          // Use the same MIME type that MediaRecorder was configured with
          const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm;codecs=opus';
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          const audioUrl = URL.createObjectURL(audioBlob);

          logger.debug('Created audio blob:', {
            size: audioBlob.size,
            type: audioBlob.type,
            chunks: audioChunksRef.current.length
          });

          const response = {
            questionId: questionsRef.current[currentQuestionIndexRef.current].id,
            questionText: questionsRef.current[currentQuestionIndexRef.current].text,
            questionAudioFile: questionsRef.current[currentQuestionIndexRef.current].audioFile,
            audioBlob,
            audioUrl,
            duration: Date.now() - recordingStartTimeRef.current,
            timestamp: new Date().toISOString()
          };

          // Discard recording if we're replaying (indicated by shouldReplayQuestionRef being true)
          const discardRecording = shouldReplayQuestionRef.current;

          if (!discardRecording) {
            setResponses(prev => [...prev, response]);
          } else {
            logger.debug('Discarding recording due to repeat request');
          }
          audioChunksRef.current = [];

          // Check if phone is still off-hook - if hung up, don't replay or process
          if (!phoneboxStateRef.current.isOffHook) {
            logger.debug('Phone hung up - skipping response processing and replay');
            shouldReplayQuestionRef.current = false; // Reset the flag
            hasValidAnswerRef.current = false; // Reset valid answer flag
            return;
          }

          // Check if we have a valid answer - only process/upload if valid
          // hasValidAnswerRef already includes fallback logic (valid speech if no confirmed answer)
          const hasValidAnswer = hasValidAnswerRef.current;
          // Replay if shouldReplay is true (which will be true if hasValidAnswer is false)
          const replayRequested = shouldReplayQuestionRef.current;

          console.log('[DEBUG] onstop handler - Decision point:', {
            hasValidAnswer,
            hasValidAnswerRef: hasValidAnswerRef.current,
            shouldReplay: shouldReplayQuestionRef.current,
            replayRequested,
            discardRecording,
            hasProcessResponseRef: !!processResponseRef.current,
            hasConfirmedAnswer: hasConfirmedAnswerRef.current,
            hasValidSpeech: hasValidSpeechRef.current,
            questionId: response.questionId
          });

          if (replayRequested) {
            console.log('[DEBUG] onstop handler - REPLAYING question:', {
              shouldReplay: shouldReplayQuestionRef.current,
              hasValidAnswer,
              hasConfirmedAnswer: hasConfirmedAnswerRef.current
            });
            logger.debug('Replaying question');
            shouldReplayQuestionRef.current = false;
            hasValidAnswerRef.current = false;
            scheduleQuestionReplay(true); // Always use manual=true for replays
          } else if (hasValidAnswer && processResponseRef.current && !discardRecording) {
            // Valid answer detected - process and upload the response
            // hasValidAnswer already includes fallback logic from stopRecording()
            console.log('[DEBUG] onstop handler - PROCESSING valid answer:', {
              hasValidAnswer,
              hasConfirmedAnswer: hasConfirmedAnswerRef.current,
              hasValidSpeech: hasValidSpeechRef.current,
              questionId: response.questionId,
              discardRecording
            });
            logger.debug('Valid answer detected - processing response for upload');
            shouldReplayQuestionRef.current = false; // Reset the flag
            hasValidAnswerRef.current = false; // Reset valid answer flag
            processResponseRef.current(response);
          } else {
            // Fallback case - shouldn't normally happen
            console.log('[DEBUG] onstop handler - FALLBACK case (unexpected state):', {
              hasValidAnswer,
              shouldReplay: shouldReplayQuestionRef.current,
              hasProcessResponseRef: !!processResponseRef.current,
              discardRecording,
              hasConfirmedAnswer: hasConfirmedAnswerRef.current
            });
            logger.debug('Unexpected state in onstop handler - hasValidAnswer:', hasValidAnswer, 'shouldReplay:', shouldReplayQuestionRef.current);
            shouldReplayQuestionRef.current = false;
            hasValidAnswerRef.current = false;
          }
        };

        mediaRecorderInitializedRef.current = true;
        logger.debug('Media recorder initialized successfully');

        // Question sequence will be started by phoneAudioEnded event or isInQuestionMode state change
        // No need to start automatically here
      } catch (error) {
        console.error('Failed to initialize media recorder:', error);
        console.error('MediaRecorder error details:', {
          name: error.name,
          message: error.message,
          supportedTypes: MediaRecorder.isTypeSupported ? {
            'audio/webm;codecs=opus': MediaRecorder.isTypeSupported('audio/webm;codecs=opus'),
            'audio/webm': MediaRecorder.isTypeSupported('audio/webm'),
            'audio/mp4': MediaRecorder.isTypeSupported('audio/mp4'),
            'audio/wav': MediaRecorder.isTypeSupported('audio/wav')
          } : 'MediaRecorder.isTypeSupported not available'
        });
        setError('Failed to initialize recording. Please try again.');
        // Automatically hang up on error
        if (!isHungUpRef.current) {
          logger.debug('Media recorder initialization error - automatically hanging up phone');
          hangup({ reason: 'error' });
        }
      }
    }
    return () => {
      // Cleanup
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }

      // Clear all timeouts
      clearAllTimeouts();

      // Stop speech recognition (handled by stopRecording)

      if (questionAudioRef.current) {
        try {
          questionAudioRef.current.pause();
          questionAudioRef.current.currentTime = 0;
          questionAudioRef.current.src = '';
        } catch (error) {
          console.warn('Error cleaning up audio on unmount:', error);
        }
      }

      setIsPlayingAudio(false);
      mediaRecorderInitializedRef.current = false;
      firstQuestionStartedRef.current = false;
    };
  }, [permissionState, stream, hasLiveStream, scheduleQuestionReplay, clearAllTimeouts, hangup]);

  // Update global question state
  useEffect(() => {
    if (setQuestionState) {
      setQuestionState({
        isActive: true,
        currentQuestion: currentQuestionIndex,
        totalQuestions: questions.length,
        state: questionState,
        isRecording,
        responses: responses.length
      });
    }
  }, [currentQuestionIndex, questionState, isRecording, responses.length, setQuestionState, questions.length]);

  // Analyze question audio for soundwaves visualization
  useEffect(() => {
    if (!phoneboxState.analyser || !phoneboxState.isPlaying || !phoneboxState.isInQuestionMode) {
      return;
    }

    const analyzeQuestionAudio = () => {
      if (phoneboxState.analyser && phoneboxState.isPlaying && phoneboxState.isInQuestionMode) {
        const bufferLength = phoneboxState.analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        phoneboxState.analyser.getByteFrequencyData(dataArray);

        const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;

        if (setAudioData) {
          setAudioData({ dataArray, average, bufferLength });
        }

        requestAnimationFrame(analyzeQuestionAudio);
      }
    };

    analyzeQuestionAudio();
  }, [phoneboxState.analyser, phoneboxState.isPlaying, phoneboxState.isInQuestionMode, setAudioData]);

  // Microphone permission UI is now handled at the App level

  // Only show error if it's a real error and we're not in the initial state before first question
  // Don't show error if we're just waiting for the first question to start (idle state before first question)
  // Don't show error modals in production (except microphone permission which is handled separately)
  const shouldShowError = error && (firstQuestionStartedRef.current || questionState !== 'idle') && process.env.NODE_ENV !== 'production';

  if (shouldShowError) {
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '20px',
        borderRadius: '10px',
        textAlign: 'center',
        zIndex: 1000
      }}>
        <h3>Error</h3>
        <p>{error}</p>
        <button
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          style={{
            background: '#007bff',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '5px',
            cursor: 'url("/icons/pointer.svg") 10 0, pointer',
            marginTop: '10px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Subtitles always show regardless of showUI */}
      <Subtitles
        detectedSpeech={detectedSpeech}
        currentSpeechConfidence={currentSpeechConfidence}
        isRecording={isRecording}
        speechRecognitionSupported={speechRecognitionSupported}
        questionState={questionState}
        currentQuestionIndex={currentQuestionIndex}
        questions={questions}
        subtitleText={currentSubtitleText}
        subtitleTimeouts={currentSubtitleTimeouts}
      />


      {/* Upload Status Indicator */}
      {showUploadIndicator && (
        <>
          <style>{`
            .question-sequence-upload-icon {
              position: fixed;
              bottom: ${isSoundOn ? '24px' : '62px'};
              right: 16px;
              width: 34px;
              height: 34px;
              z-index: 1001;
              pointer-events: none;
              transition: bottom 150ms ease, top 150ms ease, right 150ms ease;
            }
            @media (max-width: 859px) {
              .question-sequence-upload-icon {
                bottom: auto !important;
                top: 138px;
                right: 20px;
              }
            }
          `}</style>
          <img
            src="/icons/phone-uploading.svg"
            alt="Uploading audio"
            className="question-sequence-upload-icon"
          />
        </>
      )}



      {/* Debug UI only in development and when debug toggle is enabled */}
      {process.env.NODE_ENV !== 'production' && showUI && phoneboxState?.isQuestionDebugEnabled && (
        <SequenceDebug
          currentQuestionIndex={currentQuestionIndex}
          questions={questions}
          questionState={questionState}
          isRecording={isRecording}
          speechRecognitionSupported={speechRecognitionSupported}
          currentSpeechConfidence={currentSpeechConfidence}
          detectedSpeech={detectedSpeech}
          speechDebugInfo={speechDebugInfo}
          speechEndTimeoutActive={speechEndTimeoutActive}
          lastSpeechTimeRef={lastSpeechTimeRef}
          SPEECH_END_TIMEOUT={SPEECH_END_TIMEOUT}
          SPEECH_CONFIDENCE_THRESHOLD={SPEECH_CONFIDENCE_THRESHOLD}
          MIN_SPEECH_DURATION={MIN_SPEECH_DURATION}
          isSpeechRecognitionActiveRef={isSpeechRecognitionActiveRef}
          hasValidSpeechRef={hasValidSpeechRef}
          isPlayingAudio={isPlayingAudio}
          isAudioPlayingRef={isAudioPlayingRef}
          isLoadingAudio={isLoadingAudio}
          responses={responses}
          recordingStartTime={recordingStartTimeRef}
          onHangup={hangup}
          resetQuestionSequence={resetQuestionSequence}
          isUploading={isUploading}
          uploadError={uploadError}
          isTranscribing={isTranscribing}
        />
      )}

    </>
  );
};

export default QuestionSequence;
